import { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Camera, RefreshCw, Send, Filter, X, ChevronUp, ChevronDown,
         ExternalLink, Trash2, Sparkles, Plus, CreditCard, CheckCircle, Users,
         UserPlus, ShieldCheck } from 'lucide-react';
import { Pagination } from '../components/Pagination';
import { buildInvoiceImageHtml } from '../utils/invoiceImage';
import { parseCounterpartyAddress, counterpartyFieldErrors, counterpartyAddressPayload,
         describeRevolutCounterpartyError, REVOLUT_ADDRESS_LIMITS } from '../utils/addressParse';
import { documentsApi, categoriesApi, projectsApi, partnersApi,
         revolutApi, settingsApi, paymentsApi, previewUrl, companyApi, documentPdfUrl,
         isStepUpRequired, freshRequest, sendFresh } from '../services/api';
import { huFmt, isValidIban, fmtIban, maskIban } from '../utils';
import { Btn, Card, Badge, Input, Select, Alert, Modal, Spinner, DropButton,
         ConfirmDialog, SlideOver, Pill, SearchField, AssetRequiredNotice, Checkbox } from '../components/UI';
import { useToast } from '../components/Toast';
import { useStepUp } from '../contexts/StepUpContext';
import { useAuth } from '../contexts/AuthContext';
import { useBulkUpload, BulkResults } from '../components/BulkUpload';
import SyncModal from '../components/SyncModal';
import { AuthedLink, useAuthedObjectUrl } from '../components/AuthedFile';
import { currencyColor } from './Statements';
import { usePostHog } from '@posthog/react';
import { parseDocIdQuery, isLiveSearchable, SEARCH_DEBOUNCE_MS } from '../utils/docSearch';

const DEFAULT_ACCT_BODY = `Szia Marianne!

Csatolva küldöm a kért bizonylatokat.

Üdvözlettel,
Gábor`;

const STATUS_COLORS = { pending:'warning', paid:'success', overdue:'danger', cancelled:'default' };
const STATUS_LABELS = { pending:'Fizetendő', paid:'Fizetve', overdue:'Lejárt', cancelled:'Törölt' };

// A1 (2026-09-05): módosító / sztornó okirat jelzése — a NAV `invoiceOperation`-jából.
// CREATE-nél (normál számla) nem jelenik meg. A title az eredeti számlaszámot mutatja.
function NavOpBadge({ doc }) {
  const op = (doc?.nav_operation || '').toUpperCase();
  const map = {
    STORNO: { label: 'Sztornó',   color: '#ef4444',
              title: `Sztornó okirat${doc?.original_invoice_number ? ` — eredeti: ${doc.original_invoice_number}` : ''}` },
    MODIFY: { label: 'Módosító',  color: '#f59e0b',
              title: `Módosító okirat${doc?.original_invoice_number ? ` — eredeti: ${doc.original_invoice_number}` : ''}` },
  };
  const m = map[op];
  if (!m) return null;
  return (
    <span title={m.title}
      style={{flexShrink:0,fontSize:'0.62rem',fontWeight:700,letterSpacing:'0.03em',
        padding:'1px 6px',borderRadius:10,whiteSpace:'nowrap',
        color:m.color,border:`1px solid ${m.color}`,background:m.color+'18'}}>
      {m.label}
    </span>
  );
}

// K-Q1 (2026-09-06): a NAV-adat és a PDF AI-olvasata eltér — a NAV marad, a felhasználó
// ellenőriz és nyugtáz. Nyugtázott eltérésnél halvány, hogy a történet látszódjon.
function NavMismatchBadge({ doc }) {
  if (!doc?.nav_mismatch) return null;
  const seen = !!doc.nav_mismatch_seen_at;
  const m = doc.nav_mismatch;
  const parts = [];
  if (m.supplier_name) parts.push(`név: NAV „${m.supplier_name.nav}” ≠ PDF „${m.supplier_name.pdf}”`);
  if (m.warnings?.length) parts.push(...m.warnings);
  const color = seen ? '#94a3b8' : '#f59e0b';
  return (
    <span title={(seen ? 'Nyugtázott eltérés. ' : 'NAV↔PDF eltérés — ellenőrizd! ') + parts.join(' · ')}
      style={{flexShrink:0,fontSize:'0.62rem',fontWeight:700,letterSpacing:'0.03em',
        padding:'1px 6px',borderRadius:10,whiteSpace:'nowrap',
        color, border:`1px solid ${color}`, background: color+'18', opacity: seen ? 0.7 : 1}}>
      NAV≠PDF
    </span>
  );
}

// Proforma / előlegbekérő megkülönböztető badge (normál számlánál nem jelenik meg).
// A doc_type a backend enum value-ja: 'proforma' | 'előleg' | 'invoice' | ...
function DocTypeBadge({ type }) {
  const t = (type || '').toLowerCase();
  const map = {
    proforma: { label: 'Proforma',  color: '#a855f7' },
    'előleg': { label: 'Előleg',    color: '#a855f7' },
    eloleg:   { label: 'Előleg',    color: '#a855f7' },
  };
  const m = map[t];
  if (!m) return null;
  return (
    <span title="Nem adóügyi bizonylat (díjbekérő / előlegbekérő)"
      style={{flexShrink:0,fontSize:'0.62rem',fontWeight:700,letterSpacing:'0.03em',
        padding:'1px 6px',borderRadius:10,whiteSpace:'nowrap',
        color:m.color,border:`1px solid ${m.color}`,background:m.color+'18'}}>
      {m.label}
    </span>
  );
}

const SORT_FIELDS = {
  issue_date:'Kiállítva', delivery_date:'Teljesítés', due_date:'Határidő',
  paid_date:'Fizetve', supplier_name:'Kiállító', gross_amount:'Összeg',
};

// ── Modul-szintű, stabil segédek a DocumentRow-hoz ──────────────────────────
// ⚠ 2026-08-23 (wiki/proposals/pagination.md): ezek KORÁBBAN a Documents()
// komponensen belül éltek, tehát MINDEN renderen új objektum-/függvény-
// referenciát kaptak — egy React.memo-s sor props-ként ezt kapva mindig
// újrarenderelt volna, a memo hatástalan lett volna. Modul-szinten (nincs
// closure-függésük: sem props, sem state) egyszer jönnek létre.
const PAY_STATE_ICON = { paid:'✓', progress:'⏳', rejected:'✗',
                         awaiting:'⏸', cancelled:'⊘' };
const PAY_STATE_COLOR = { paid:'#22c55e', progress:'#f59e0b', rejected:'var(--danger)',
                          awaiting:'#60a5fa', cancelled:'var(--text-muted)' };
const fmtDate = (v) => v ? v.slice(0,10).replace(/-/g,'.') + '.' : '–';
const fmtAmt = (v, cur='HUF') => v == null ? '–' : `${huFmt(v, cur === 'HUF' ? 0 : 2)} ${cur}`;
const tdS = { padding:'0.55rem 0.75rem', color:'var(--text)', verticalAlign:'middle', fontSize:'0.8rem',
  borderBottom:'1px solid var(--border-dim)' };

/* Egy bizonylat-sor — MEMOIZÁLT (wiki/proposals/pagination.md, "1000-per-page
 * decision is a measurement gate"). A sor checkbox+IBAN-jelző+badge+3 dátum+
 * fizetési-állapot+összeg+kategória+akció cellákat renderel, ~10 beágyazott
 * elemmel — 1000 sornál ez a legdrágább rész. `React.memo` + PRIMITÍV props
 * (`isSelected`, `anySelected` — NEM a teljes `selected` tömb) azt jelenti,
 * hogy egyetlen sor kijelölése csak AZT az egy sort rendereli újra, nem mind
 * az 1000-et. ⚠ Ez a mérési kapu ELSŐ fele — a második (valós 1000-soros
 * render/interakció-mérés) még nem történt meg, lásd a proposal "measurement
 * gate" szakaszát; eddig csak a szerkezeti feltétel készült el. */
const DocumentRow = memo(function DocumentRow({
  doc: d, ibanStatus, isPayable, isSelected, anySelected, onToggle, onNavigate, onPay,
}) {
  const rowBg = isSelected ? 'rgba(59,130,246,0.08)' : 'transparent';
  return (
    <tr
      style={{cursor:'pointer',transition:'background 0.1s',background:rowBg}}
      onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
      onMouseLeave={e=>e.currentTarget.style.background=rowBg}
      onClick={()=>anySelected ? onToggle(d.id) : onNavigate(d.id)}>
      <td style={{...tdS, boxShadow: isSelected ? 'inset 2px 0 0 var(--primary)' : undefined}}
        onClick={e=>e.stopPropagation()}>
        <input type="checkbox" style={{width:14,height:14,accentColor:'var(--primary)'}}
          checked={isSelected}
          onChange={()=>onToggle(d.id)}/>
      </td>
      <td style={tdS}>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          {ibanStatus && (
            <span
              title={
                ibanStatus==='found'    ? 'Partner létezik Revoluton (IBAN egyezik)' :
                ibanStatus==='missing'  ? 'Partner nem található Revoluton' :
                ibanStatus==='mismatch' ? 'IBAN eltérés: a giro-ból számított IBAN ≠ a számlán szereplő IBAN' :
                                         'Hibás IBAN ellenőrző szám – nem utalható'
              }
              style={{flexShrink:0,width:7,height:7,borderRadius:'50%',
                background:
                  ibanStatus==='found'    ? 'var(--accent)' :
                  ibanStatus==='mismatch' ? '#f59e0b' :
                  ibanStatus==='invalid'  ? '#f59e0b' :
                                           'rgba(255,255,255,0.18)',
                border:
                  ibanStatus==='found'    ? 'none' :
                  ibanStatus==='mismatch' ? 'none' :
                  ibanStatus==='invalid'  ? 'none' :
                                           '1px solid rgba(255,255,255,0.25)',
              }}/>
          )}
          <div style={{minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:6,maxWidth:230}}>
              <span title={`Bizonylat #${d.id}`}
                style={{fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {(d.direction === 'outgoing' ? d.customer_name : d.supplier_name)
                  ||<span style={{color:'var(--text-dim)'}}>Ismeretlen</span>}
              </span>
              <DocTypeBadge type={d.doc_type}/>
              <NavOpBadge doc={d}/>
              <NavMismatchBadge doc={d}/>
            </div>
            {d.invoice_number&&(
              <div style={{fontSize:'0.68rem',color:'var(--text-muted)',fontFamily:'var(--mono)'}}>
                {d.invoice_number}
              </div>
            )}
          </div>
        </div>
      </td>
      <td style={tdS}><span style={{fontFamily:'var(--mono)'}}>{fmtDate(d.issue_date)}</span></td>
      <td style={tdS}><span style={{fontFamily:'var(--mono)'}}>{fmtDate(d.delivery_date)}</span></td>
      <td style={tdS}>
        <span style={{fontFamily:'var(--mono)',
          color:d.due_date&&(d.status==='pending'||d.status==='overdue')&&new Date(d.due_date)<new Date()
            ?'var(--danger)':'inherit'}}>
          {fmtDate(d.due_date)}
        </span>
      </td>
      {/* Fizetve oszlop – pay gomb VAGY dátum VAGY draft várakozik */}
      <td style={tdS} onClick={e=>e.stopPropagation()}>
        {d.status==='paid'
          ? <span style={{fontFamily:'var(--mono)',fontSize:'0.78rem',color:'var(--accent)',
              display:'inline-flex',alignItems:'center',gap:4}}
              title={d.bank_matched ? 'Bank kivonatból megerősítve' : undefined}>
              {d.bank_matched && <span style={{fontSize:'0.7rem'}}>🏦</span>}
              ✓ {fmtDate(d.paid_date)}
            </span>
          /* ⚠ MÁR VAN UTALÁS-NYOM — gomb helyett ÁLLAPOT, a backend
             egyesített szótárából. Enélkül a bizonylat gomb nélkül,
             „fizetetlen"-ként állt ott, és a felhasználó nem tudta,
             hogy a tétel már a bankban van (a SEPA napokig
             „Folyamatban" marad; PAID csak elszámolás után). */
          : d.payment_state
            ? <span title={`${d.payment_state.label}`
                  + ` · ${d.payment_state.bank === 'raiffeisen' ? 'Raiffeisen' : 'Revolut'}`
                  + (d.payment_state.reference
                      ? ` · banki referencia: ${d.payment_state.reference}` : '')}
                style={{display:'inline-flex',alignItems:'center',gap:4,
                  color: PAY_STATE_COLOR[d.payment_state.state] || 'var(--text-muted)',
                  fontSize:'0.71rem',fontWeight:600,cursor:'default',
                  whiteSpace:'nowrap'}}>
                {PAY_STATE_ICON[d.payment_state.state] || '•'} {d.payment_state.label}
              </span>
          : ibanStatus==='mismatch'
            ? <span title="A giro-ból számított IBAN eltér a számlán szereplő IBAN-tól – ellenőrzés szükséges"
                style={{color:'#f59e0b',fontSize:'0.71rem',fontWeight:600,cursor:'default'}}>
                ⚠ IBAN eltérés
              </span>
          : isPayable
            ? <button
                title="Revolut utalás indítása"
                onClick={e=>{e.stopPropagation();onPay(d);}}
                style={{display:'inline-flex',alignItems:'center',gap:4,
                  background:'rgba(59,130,246,0.13)',border:'1px solid rgba(59,130,246,0.4)',
                  borderRadius:'var(--radius)',padding:'0.18rem 0.48rem',
                  color:'var(--primary)',cursor:'pointer',fontSize:'0.71rem',fontWeight:600,
                  whiteSpace:'nowrap'}}>
                <CreditCard size={11}/> Utal
              </button>
            : <span style={{color:'var(--text-dim)',fontSize:'0.75rem'}}>–</span>
        }
      </td>
      <td style={{...tdS,textAlign:'right',fontFamily:'var(--mono)'}}>
        {d.payable_amount != null && d.payable_amount !== d.gross_amount ? (
          <div>
            <div style={{fontWeight:700}}>{fmtAmt(d.payable_amount,d.currency)}</div>
            <div style={{fontWeight:400,color:'var(--text-muted)'}}>
              (Σ {fmtAmt(d.gross_amount,d.currency)})
            </div>
          </div>
        ) : (
          <span style={{fontWeight:500}}>{fmtAmt(d.gross_amount,d.currency)}</span>
        )}
      </td>
      <td style={tdS}>
        {d.category?<Badge>{d.category}</Badge>:<span style={{color:'var(--text-dim)'}}>–</span>}
      </td>
      <td style={{...tdS,whiteSpace:'nowrap'}} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span title={d.accountant_last_sent?`Könyvelőnek küldve: ${d.accountant_last_sent.slice(0,10)}`:'Még nem küldve'}
            style={{width:20,height:20,borderRadius:'50%',display:'flex',alignItems:'center',
              justifyContent:'center',fontSize:'0.65rem',fontWeight:700,cursor:'default',
              background:d.accountant_last_sent?'rgba(16,185,129,0.2)':'rgba(255,255,255,0.05)',
              color:d.accountant_last_sent?'var(--accent)':'var(--text-dim)',
              border:`1px solid ${d.accountant_last_sent?'var(--accent)':'var(--border)'}`}}>K</span>
          {(d.pdf_onedrive_url || d.pdf_filename)
            ?<AuthedLink href={documentPdfUrl(d.id)}
                style={{color:'var(--primary)',fontSize:'0.75rem',fontWeight:600}}
                title="PDF megnyitása">PDF</AuthedLink>
            :<button type="button" onClick={(e)=>{e.stopPropagation(); openGeneratedImage(d);}}
                title="Nincs PDF — számlakép a tárolt adatból (NAV/Billingo tételsorok, ÁFA-bontás). NEM hiteles bizonylat."
                style={{background:'none',border:'1px dashed var(--border)',borderRadius:4,color:'var(--text-muted)',
                  fontSize:'0.68rem',padding:'0 5px',cursor:'pointer'}}>kép</button>}
        </div>
      </td>
    </tr>
  );
});

// D6 (2026-09-06): generált számlakép a tárolt adatból — külön ablak, vízjelezve. Nem hiteles.
async function openGeneratedImage(d) {
  const w = window.open('', '_blank');           // előre nyitjuk, hogy a popup-blokkoló ne fogja
  try {
    const [doc, ld] = await Promise.all([documentsApi.get(d.id), documentsApi.lines(d.id)]);
    const html = buildInvoiceImageHtml(doc, ld.lines, ld.vat_summary);
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  } catch (e) {
    if (w) w.close();
    alert('A számlakép nem tölthető be.');
  }
}

const pad = n => String(n).padStart(2, '0');

// Helyi (nem UTC) YYYY-MM-DD – előkitöltött dátummezőkhöz
const ymd = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
const todayYmd = () => ymd(new Date());
const daysAgoYmd = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return ymd(d); };

// Idő-szűrő → {date_from, date_to, date_field} mapping.
// A field MELYIK dátummezőre szűrjön (default: teljesítés = delivery_date).
function getDateFilterParams(key, range, field = 'delivery_date') {
  const now = new Date();
  switch (key) {
    case 'this-month': {
      const y = now.getFullYear(), m = now.getMonth() + 1;
      return { date_from:`${y}-${pad(m)}-01`, date_to:`${y}-${pad(m)}-${new Date(y,m,0).getDate()}`, date_field:field };
    }
    case 'last-month': {
      const d = new Date(now.getFullYear(), now.getMonth(), 0);
      const y = d.getFullYear(), m = d.getMonth() + 1;
      return { date_from:`${y}-${pad(m)}-01`, date_to:`${y}-${pad(m)}-${d.getDate()}`, date_field:field };
    }
    case 'this-year':
      return { date_from:`${now.getFullYear()}-01-01`, date_to:`${now.getFullYear()}-12-31`, date_field:field };
    case 'custom':
      if (!range?.from && !range?.to) return {};
      return {
        ...(range.from ? { date_from: range.from } : {}),
        ...(range.to   ? { date_to:   range.to   } : {}),
        date_field: field,
      };
    default: return {};
  }
}

export default function Documents() {
  // D26 (2026-09-27): a bér-bizonylatot a backend nem-ADMIN-nak nem adja ki — a felület csak
  // KIMONDJA, hogy a lista bér nélküli (U2, INV-35), nem ő rejt.
  const { isAdmin } = useAuth();
  const { requireFreshStepUp: requireSendStepUp } = useStepUp();
  const qc = useQueryClient();
  const toast = useToast();
  const posthog = usePostHog();
  const scanRef = useRef();
  // Route-olt részlet-panel: /documents/:docId → a panel mélylinkelhető,
  // a böngésző vissza-gombja zárja, F5 után is visszanyílik.
  const { docId } = useParams();
  const navigate = useNavigate();
  const closePanel = () => navigate('/documents');
  // D-WP-10: a chat `open_payment_modal` UI-intentje ide navigál
  // (`/documents?pay=1,2,3`) — lásd az ezt feloldó `useEffect`-et lentebb.
  const [searchParams, setSearchParams] = useSearchParams();

  // Partner-szűrés a Partnerek modulból (`/documents?partner_id=…`, 2026-09-16).
  // ⚠ A FELOLDOTT partnerre szűr (`documents.partner_id`), nem névre — tehát a
  // névváltozatokat is összefogja, és kimenő bizonylaton a VEVŐT jelenti (K2).
  const partnerId = searchParams.get('partner_id') || null;

  // Alapszűrők (toggle Set: 'incoming'|'outgoing'|'baj'). Üres → "válassz egyet" üzenet.
  // ⚠ Partner-szűréssel érkezve MINDHÁROM alapszűrő nyitva indul: a partner bizonylatai
  // bármelyik körbe eshetnek, és az alapértelmezett „csak bejövő" némán elrejtené egy
  // vevő-partner összes számláját („nincs bizonylata" — pedig van).
  const [baseFilters, setBaseFilters] = useState(
    () => new Set(partnerId ? ['incoming', 'outgoing', 'baj'] : ['incoming']));
  // Idő-szűrő: 'this-month'|'last-month'|'this-year'|'custom'|null
  const [dateFilter, setDateFilter] = useState(null);
  // A custom tartomány mezői előre kitöltve az elmúlt 7 nappal (csak akkor szűr,
  // ha a felhasználó használja – dateFilter='custom').
  const [dateRange, setDateRange] = useState({ from: daysAgoYmd(7), to: todayYmd() });
  // Melyik dátummezőre szűrjenek a gombok ÉS az egyedi tartomány. Default: teljesítés.
  const [dateField, setDateField] = useState('delivery_date');
  // Egyéb toggle szűrők (Set): 'no-pdf', 'unpaid', 'overdue'
  const [extraFilters, setExtraFilters] = useState(() => new Set());
  // Deviza-szűrők (Set: 'HUF','EUR','USD'). Üres → minden deviza.
  const [currencyFilters, setCurrencyFilters] = useState(() => new Set());

  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({ status:'', category:'', currency:'', q:'' });
  const [showFilters, setShowFilters] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [sortField, setSortField] = useState('issue_date');
  const [sortDir, setSortDir] = useState('desc');
  const [payModal, setPayModal] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Lapozás (wiki/proposals/pagination.md) — `viewMode` a page-size Select értéke
  // ('100'|'year'), a `page` 1-alapú. Nézetváltásnál a hívó explicit
  // 1-re állítja a page-et (lásd handleViewModeChange) — nem effektussal, mert
  // a nézetváltás ritka és explicit felhasználói művelet.
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('100');

  // Származtatott flag-ek
  const incomingActive = baseFilters.has('incoming');
  const outgoingActive = baseFilters.has('outgoing');
  const bajActive      = baseFilters.has('baj');
  const noBaseFilter   = baseFilters.size === 0;
  // Régi `isOutgoing` kompatibilitás: "csak kimenőre fókuszálunk"
  const outgoingOnly = outgoingActive && !incomingActive && !bajActive;
  const incomingOnly = incomingActive && !outgoingActive && !bajActive;

  const dateParams = useMemo(() => getDateFilterParams(dateFilter, dateRange, dateField), [dateFilter, dateRange, dateField]);

  // A SZŰRŐK jele — SZÁNDÉKOSAN nem tartalmazza a page/viewMode-ot. Erre a
  // jelre figyel a lenti useEffect, hogy egy szűrő-változás 1-re állítsa a
  // page-et (különben egy leszűkített szűrő mellett a felhasználó egy már
  // nem létező oldalon maradhat, üres listát látva ok nélkül).
  const filterParams = useMemo(() => ({
    base_filters: [...baseFilters].join(',') || undefined,
    ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)),
    ...(currencyFilters.size > 0 ? { currency_in: [...currencyFilters].join(',') } : {}),
    ...(extraFilters.has('no-pdf') ? { has_pdf: false } : {}),
    ...(extraFilters.has('nav-mismatch') ? { nav_mismatch: true } : {}),
    ...(extraFilters.has('unpaid') ? { unpaid:  true  } : {}),
    ...(extraFilters.has('overdue')? { overdue: true  } : {}),
    ...(partnerId ? { partner_id: partnerId } : {}),
    ...dateParams,
  }), [baseFilters, filters, currencyFilters, extraFilters, dateParams, partnerId]);

  useEffect(() => { setPage(1); }, [filterParams]);

  const queryParams = useMemo(() => ({
    ...filterParams,
    page,
    page_size: viewMode,
  }), [filterParams, page, viewMode]);

  const handleViewModeChange = (v) => { setViewMode(v); setPage(1); };

  // A partner-szűrő NEVE — a chip nem `#id`-t mutat, mert az semmit nem mond.
  const { data: filterPartner } = useQuery({
    queryKey: ['partner', partnerId],
    queryFn: () => partnersApi.get(partnerId),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });
  const clearPartnerFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('partner_id');
    setSearchParams(next, { replace: true });
  };

  const { data: docsPage, isLoading, isError: docsError, refetch: refetchDocs } = useQuery({
    queryKey: ['documents', queryParams],
    queryFn: () => documentsApi.list(queryParams),
    enabled: !noBaseFilter,
  });
  const rawDocs = docsPage?.items ?? [];

  /* ⚠⚠ AUTO-POLLER a lap megnyitásakor (2026-08-12, Gábor kérése: „a normál
   * lekérdezésnek már régen működnie kéne automatikusan").
   *
   * Miért ITT is, és nem csak induláskor: egy `ACTC`-n álló SEPA-utalás napokig
   * nem végleges. Ha csak az alkalmazás indulásakor kérdeznénk rá, a felhasználó
   * egy hosszan futó szerveren SOHA nem látná a lezárást.
   *
   * ⚠ A FÉK A BACKENDEN van (60 s), nem itt: egy kliens-oldali időzítő fülönként
   *   külön számolna, és három megnyitott fül háromszor verné a bankot (a
   *   Raiffeisen 429-cel rate-limitel). Ezért itt NEM forszírozunk — a szerver
   *   dönti el, hogy tényleg megkérdezi-e.
   * ⚠ A `mountedRef` guard kell: a frissítés érvényteleníti a `documents` query-t,
   *   ami újrarendereléshez vezet — enélkül végtelen ciklus lenne.
   * ⚠ CÉG-SCOPE: nincs `company_id` paraméter, és nem is kell. A session jelölése
   *   (get_db) + a payment-táblák FORCE RLS-e szűri. Mérve: ugyanez a kód cég 1-nél
   *   7 nyitott tételt lát, cég 2-nél 0-t.
   */
  const pollGuardRef = useRef(false);
  useEffect(() => {
    if (pollGuardRef.current) return;
    pollGuardRef.current = true;
    (async () => {
      try {
        const r = await paymentsApi.refreshPending();
        // Csak akkor kérünk újra listát, ha tényleg történt banki lekérdezés —
        // a fék által kihagyott futás után nincs mit újratölteni.
        // ⚠ 2026-08-25: a ['document', id] részlet-cache (DocSlideOver) is
        // frissítendő, különben a slide-over egy már lezárt banki állapotot
        // (payment_state) is réginek mutathat - lásd a #249-es élő hibát.
        if (r?.checked) {
          qc.invalidateQueries({ queryKey: ['documents'] });
          qc.invalidateQueries({ queryKey: ['document'] });
        }
      } catch { /* csendes: a státusz-frissítés hibája ne törje el az oldalt */ }
    })();
  }, [qc]);

  // A panel bizonylata KÜLÖN, egyedi lekérés – így a mélylink akkor is működik,
  // ha a bizonylat nincs benne az aktuális szűrt listában.
  const { data: panelDoc, isError: panelErr } = useQuery({
    queryKey: ['document', docId],
    queryFn: () => documentsApi.get(docId),
    enabled: !!docId,
  });
  useEffect(() => {
    if (panelErr) { toast.error('A bizonylat nem található.'); navigate('/documents', { replace:true }); }
  }, [panelErr, toast, navigate]);

  const { data: revolutCounterparties = [] } = useQuery({
    queryKey: ['revolut-counterparties'],
    queryFn: revolutApi.counterparties,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  // ⚠ A korábbi „payment-draft auto-szinkron" MEGSZŰNT (2026-08-13). Két oka volt a
  //   törlésnek, és a második a fontosabb:
  //   1. REDUNDÁNS — a lap megnyitásakor amúgy is fut a `refresh-pending` poller
  //      (lentebb), ami MINDEN nem-végleges utalást frissít, bankfüggetlenül.
  //   2. GYENGÉBB VOLT — a draft legjeit `reference` + összeg + deviza alapján
  //      párosította, és ha nem talált egyezőt, a bizonylatot „failed"-ként kezelte.
  //      A mostani út (`revolut_connector.refresh_row`) többszörös egyezésnél NEM
  //      TIPPEL, épp azért, hogy ne állítson téves bizonylatot `PAID`-re.

  const ibanMap = useMemo(() => {
    const map = new Map();
    revolutCounterparties.forEach(cp => {
      (cp.accounts || []).forEach(acc => {
        const iban = acc.iban?.replace(/\s/g, '').toUpperCase();
        if (iban) map.set(iban, cp);
      });
    });
    return map;
  }, [revolutCounterparties]);

  // Gépelés közben (késleltetve) a SZERVER keres — lásd `utils/docSearch.js`, miért.
  useEffect(() => {
    const s = searchInput.trim();
    if (!isLiveSearchable(s)) return undefined;
    const t = setTimeout(() => setFilters(f => (f.q === s ? f : {...f, q:s})), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const suggestions = useMemo(() => {
    if (!searchInput || searchInput.length < 2) return [];
    // Javaslat CSAK a már erre a szövegre kapott szerver-eredményből: a betöltött
    // (első 100 soros) alapnézetből épített lista hamisan „nincs ilyen"-t mondott.
    if ((filters.q || '') !== searchInput.trim()) return [];
    if (parseDocIdQuery(searchInput)) return [];
    // Ékezet- és kisbetű-érzéketlen (a 'gep' a 'GÉP'-et is megtalálja)
    const fold = s => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const needle = fold(searchInput);
    const items = new Set();
    rawDocs.forEach(d => {
      const name = d.direction === 'outgoing' ? d.customer_name : d.supplier_name;
      if (name && fold(name).includes(needle)) items.add(name);
      if (d.invoice_number && fold(d.invoice_number).includes(needle)) items.add(d.invoice_number);
    });
    return [...items].slice(0, 8);
  }, [searchInput, rawDocs, filters.q]);

  const { data: filterCats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
    staleTime: 5 * 60 * 1000,
  });

  const proformaTypes = ['proforma', 'előleg', 'eloleg'];
  const docs = [...rawDocs]
    .filter(d => !extraFilters.has('proforma')
      || proformaTypes.includes((d.doc_type || '').toLowerCase()))
    .sort((a, b) => {
      const av = a[sortField] ?? '', bv = b[sortField] ?? '';
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp size={12} style={{opacity:0.2}}/>;
    return sortDir === 'desc'
      ? <ChevronDown size={12} style={{color:'var(--primary)'}}/>
      : <ChevronUp size={12} style={{color:'var(--primary)'}}/>;
  };

  const thStyle = (field) => ({
    padding:'0.6rem 0.75rem', color:'var(--text-muted)', fontWeight:500,
    fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em',
    whiteSpace:'nowrap', cursor: field ? 'pointer' : 'default', userSelect:'none',
    borderBottom:'1px solid var(--border)',
  });

  const bulk = useBulkUpload();   // ömlesztett feltöltés: match-or-create
  const scanMut = useMutation({
    mutationFn: (file) => { const fd = new FormData(); fd.append('file', file); return documentsApi.scan(fd); },
    onSuccess: () => {
      posthog?.capture('document_scanned');
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    // 2026-09-29: a backend a bankszámla-kivonatot elutasítja (`is_bank_statement`, 422) —
    // a hibát nem nyeljük el, hanem megmutatjuk, és a Kivonatok oldalra irányítunk.
    onError: (err) => {
      const d = err?.response?.data?.detail;
      if (d?.code !== 'is_bank_statement') {
        toast.error((typeof d === 'string' && d) || 'A feltöltés sikertelen.');
      }
    },
  });
  const scanDetail = scanMut.error?.response?.data?.detail;
  // Q2 (2026-09-06): tömeges státusz a kijelölteken. A backend csak a „várható" jelenlegi
  // státuszból vált (PAID-re PENDING/OVERDUE-ról), a többit kihagyja és megnevezi.
  const bulkStatusMut = useMutation({
    mutationFn: ({ids, status, extra}) => documentsApi.bulkStatus(ids, status, extra),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setSelected([]);
      const skipped = res.skipped?.length || 0;
      toast.success(`${res.updated} bizonylat → ${STATUS_LABELS[res.status] || res.status}`
        + (skipped ? ` · ${skipped} kihagyva (más státuszból nem váltunk)` : ''));
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'A tömeges státusz-beállítás sikertelen.'),
  });
  const [bulkConfirm, setBulkConfirm] = useState(null);   // {title, message, danger, confirmLabel, action}
  // A lejárat-söprés kapuja (2026-09-28): nyitott kapunál a kézzel „Fizetendő”-re állított,
  // lejárt határidejű bizonylatot a napi söprés újra „Lejárt”-ra írja — ezt a megerősítő
  // ablak egy mondattal jelzi. Ugyanaz a `['settings']` lekérés, mint a fizetési ablakban.
  const { data: docSettings = {} } = useQuery({
    queryKey: ['settings'], queryFn: settingsApi.get, staleTime: 60_000,
  });
  const overdueSweepOn = docSettings['documents.overdue_sweep_enabled'] === 'true';
  const bulkSetStatus = (status) => {
    const label = STATUS_LABELS[status] || status;
    setBulkConfirm({
      title: `Kijelöltek → ${label}`,
      message: `${selected.length} kijelölt bizonylat státusza „${label}” lesz. `
        + (status === 'paid' ? 'Kifizetés dátuma nem kerül be (nem ismert). ' : '')
        + 'A régi státusz a jegyzetbe kerül. Csak a várható jelenlegi státuszból váltunk, a többit kihagyjuk.'
        + (status === 'pending' && overdueSweepOn
          ? ' Lejárt határidejű bizonylat a következő éjszakai ellenőrzéskor újra »Lejárt« lesz.' : ''),
      confirmLabel: 'Beállítás',
      danger: status === 'cancelled',
      action: () => bulkStatusMut.mutate({ ids: selected, status,
        extra: { note: status === 'paid' ? 'kijelöléssel rendezettnek jelölve' : undefined } }),
    });
  };
  const updateMut = useMutation({
    mutationFn: ({id, data}) => documentsApi.update(id, data),
    onSuccess: (updated) => {
      qc.setQueriesData({ queryKey: ['documents'] }, (old) =>
        old ? { ...old, items: old.items.map(d => d.id === updated.id ? {...d,...updated} : d) } : old
      );
      qc.invalidateQueries({ queryKey: ['document', String(updated.id)] });
      closePanel();
      toast.success('Bizonylat mentve');
    },
    onError: (err) =>
      toast.error('Mentés sikertelen: ' + (err?.response?.data?.detail || err.message)),
  });
  const [showAcctModal, setShowAcctModal] = useState(false);
  const [acctBody, setAcctBody] = useState(DEFAULT_ACCT_BODY);
  const [acctResult, setAcctResult] = useState(null);   // {ok, sent, attached, to} | {error}
  const [acctGreen, setAcctGreen] = useState(false);    // 5 mp-es zöld pipa a gombon
  // ⚠ PSU-5 (2026-09-26, tulajdonosi döntés): a kijelölt bizonylatok küldése FRISS, egyszeri
  //   passkey-jegyet kér (`FRESH_ACTION_ENDPOINTS`); a ceremónia pontosan ezt a törzset jelenti
  //   be, és a `sendFresh` pontosan ezt küldi — eltérés = 403.
  const accountantMut = useMutation({
    mutationFn: async (email_body) => {
      const req = freshRequest('POST /accountant/send', { body: { doc_ids: selected, email_body } });
      if (!await requireSendStepUp('A bizonylatok könyvelőnek küldése előtt', [req])) {
        throw new Error('A küldés megszakítva — a könyvelőnek küldéshez passkey-megerősítés kell.');
      }
      return sendFresh(req);
    },
    onSuccess: (data) => {
      posthog?.capture('documents_sent_to_accountant', {
        doc_count: selected.length,
        attached: data?.attached,
      });
      setAcctResult({ ok: true, ...data });
      qc.invalidateQueries({ queryKey: ['documents'] });
      setAcctGreen(true);
      setTimeout(() => { setAcctGreen(false); setSelected([]); }, 5000);
    },
    onError: (err) => setAcctResult({
      error: err?.response?.data?.detail || err.message || 'Küldési hiba' }),
  });

  const handleScanFile = (e) => {
    const file = e.target.files?.[0];
    if (file) scanMut.mutate(file);
    e.target.value = '';
  };

  // Utalandó összeg: a Fizetendő (előleg-beszámítás után), ha van; különben a teljes bruttó.
  const payAmt = (d) => (d.payable_amount != null ? d.payable_amount : d.gross_amount) || 0;
  // Kijelölt összegek DEVIZÁNKÉNT (pénzt mozgató műveletnél nem mosható össze)
  const selectedByCurrency = useMemo(() => {
    const m = new Map();
    docs.filter(d => selected.includes(d.id)).forEach(d => {
      const cur = d.currency || 'HUF';
      m.set(cur, (m.get(cur) || 0) + payAmt(d));
    });
    return [...m.entries()];
  }, [docs, selected]);
  // fmtDate / fmtAmt / tdS: modul-szinten (lásd DocumentRow fölött) — a
  // memoizált sor stabil referenciát vár, ha bővülne a props-listájuk.

  // ⚠ `useCallback([])` — a `DocumentRow` React.memo-ja csak akkor ér valamit,
  // ha ezek a callback-ek NEM kapnak minden renderen új referenciát. A
  // funkcionális `setSelected(prev => ...)` alak miatt nincs szükség a
  // `selected`-re a dependency-listában.
  const handleToggleRow = useCallback((id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);
  const handleNavigateRow = useCallback((id) => { navigate(`/documents/${id}`); }, [navigate]);
  const handlePayRow = useCallback((doc) => { setPayModal({docs:[doc]}); }, []);

  const toggleBaseFilter = (key) => {
    setBaseFilters(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
    setSelected([]);
  };
  const toggleExtra = (key) => {
    setExtraFilters(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  // K-Q1: a Dashboard teendője `?nav_mismatch=1`-gyel érkezik → a szűrő előre bekapcsolva.
  useEffect(() => {
    if (searchParams.get('nav_mismatch') === '1') {
      setExtraFilters(prev => new Set([...prev, 'nav-mismatch']));
    }
  }, [searchParams]);
  const toggleCurrency = (cur) => {
    setCurrencyFilters(prev => {
      const next = new Set(prev);
      next.has(cur) ? next.delete(cur) : next.add(cur);
      return next;
    });
  };

  /* A cél-IBAN ismertsége. ⚠ 2026-08-12-ig `getRevolutStatus` volt a neve, holott
   * MINDEN bankra ez dönti el, hogy utalható-e a bizonylat — egy Raiffeisen-utalás
   * jogosultságát is egy „Revolut" nevű függvény mondta meg. Gábor jogos
   * kifogása: a provider neve ne szivárogjon az üzleti logikába. */
  const getIbanStatus = (doc) => {
    if (doc.iban_mismatch) return 'mismatch';
    const iban = doc.bank_account_iban?.replace(/\s/g, '').toUpperCase();
    if (!iban) return null;
    if (!isValidIban(iban)) return 'invalid';
    return ibanMap.has(iban) ? 'found' : 'missing';
  };

  const canPay = (doc) => {
    // ⚠⚠ MÁR ELINDULT UTALÁS → NINCS „Utal" gomb. A döntést a BACKEND mondja meg
    //    (`payment_state.blocks_new_payment`), ugyanabból a szótárból, amit a
    //    dupla-utalás kapu használ — így a gomb és a 409 nem tud szétcsúszni.
    //    ⚠ Korábban ez a sor `doc.payment_draft_id`-t vizsgált, ami CSAK a Revolut
    //      draftot jelzi: egy beküldött Raiffeisen-utalás után a felület újra
    //      felkínálta az utalást (2026-08-11, #245/#219).
    //    ⚠ Az `Elutasítva` és a `Visszavonva` SZÁNDÉKOSAN nem tilt: egy bukott
    //      utalás után újra lehet próbálni.
    if (doc.payment_state?.blocks_new_payment) return false;
    if ((doc.gross_amount || 0) <= 0) return false;  // jóváírás / credit — nem utalandó
    if (doc.direction === 'outgoing') return false;
    const st = getIbanStatus(doc);
    return ['pending','overdue'].includes(doc.status) &&
      (st === 'found' || st === 'missing');
  };

  // PAY_STATE_ICON / PAY_STATE_COLOR: modul-szinten (lásd DocumentRow fölött).

  const payableDocs = docs.filter(d => selected.includes(d.id) && canPay(d));

  // ⚠⚠ D-WP-10 — a chat `open_payment_modal` UI-intentjének feloldása. UGYANAZT
  // a mintát használja, mint a `panelDoc` mélylink (fentebb): EGYEDI lekérés
  // ID-nként, mert a bizonylatok lehet, hogy nincsenek benne az aktuális
  // szűrt/lapozott listában. A `canPay()`-en való átszűrés a MEGLÉVŐ, éles
  // szabály — nem új kapu csak a chat-eredetű megnyitásra (Q-D2 4. pont: a
  // kiválasztás nem jöhet a modell kimenetéből, hanem ellenőrizhető szűrőből;
  // itt ez a szűrő maga a mindenhol használt `canPay()`). A `pay` paramétert
  // sikeres VAGY sikertelen feloldás után is töröljük, hogy vissza-navigálásra
  // ne nyíljon meg újra a modal.
  useEffect(() => {
    const payParam = searchParams.get('pay');
    if (!payParam) return;
    const ids = payParam.split(',').map(s => parseInt(s, 10)).filter(n => !Number.isNaN(n));
    let cancelled = false;
    (async () => {
      const fetched = ids.length
        ? await Promise.all(ids.map(id => documentsApi.get(id).catch(() => null)))
        : [];
      if (cancelled) return;
      const payable = fetched.filter(Boolean).filter(canPay);
      if (payable.length) {
        setPayModal({ docs: payable });
      } else {
        toast.error('A javasolt bizonylatok egyike sem utalható.');
      }
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('pay');
        return next;
      }, { replace: true });
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:8}}>
        <div>
          <h1 style={{fontSize:'1.25rem',fontWeight:700}}>Bizonylatok</h1>
          <p style={{fontSize:'0.8rem',color:'var(--text-muted)',marginTop:2}}>{docs.length} bizonylat</p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <Btn variant="ghost" size="sm" icon={Filter} onClick={()=>setShowFilters(s=>!s)}>Szűrők</Btn>
          <Btn variant="ghost" size="sm" icon={RefreshCw} onClick={()=>setShowSyncModal(true)}>Szinkronizálás</Btn>
          <input ref={scanRef} type="file" accept="image/*,application/pdf" capture="environment"
            style={{display:'none'}} onChange={handleScanFile}/>
          <Btn variant="outline" size="sm" icon={Camera} loading={scanMut.isPending}
            onClick={()=>scanRef.current.click()}>Scan</Btn>
          <DropButton onFiles={bulk.process} busy={bulk.busy}/>
        </div>
      </div>

      <BulkResults items={bulk.items} notice={bulk.notice} progress={bulk.progress}
        busy={bulk.busy} onCancel={bulk.cancel} onClear={bulk.clear}
        onResolveAttach={bulk.resolveAttach} onResolveCreate={bulk.resolveCreate}
        onResolveDiscard={bulk.resolveDiscard}/>
      {scanMut.isSuccess && <Alert type="success">Bizonylat feltöltve!</Alert>}
      {scanDetail?.code === 'is_bank_statement' && (
        <Alert type="warning">
          {scanDetail.message} <Link to="/statements">Tovább a Kivonatokhoz →</Link>
        </Alert>
      )}

      {/* ── Alapszűrők (Bejövő / Kimenő / B.A.J.) ─────────────────────── */}
      <div style={{display:'flex',gap:10,marginBottom:'0.75rem',flexWrap:'wrap',alignItems:'center'}}>
        <Pill size="lg" color="#10b981" active={incomingActive}
          onClick={()=>toggleBaseFilter('incoming')}
          title="Bejövő bizonylatok">⬇ Bejövő</Pill>
        <Pill size="lg" color="#3b82f6" active={outgoingActive}
          onClick={()=>toggleBaseFilter('outgoing')}
          title="Kimenő bizonylatok">⬆ Kimenő</Pill>
        <Pill size="lg" color="#f59e0b" active={bajActive}
          onClick={()=>toggleBaseFilter('baj')}
          title={isAdmin ? 'Bérek, Adók, Járulékok'
            : 'Adók és járulékok — a bér-bizonylatokat csak cég-adminisztrátor látja'}>⚖ B.A.J.</Pill>

        {/* Intelligens keresőmező – az alapszűrők mellett, jobbra igazítva */}
        <SearchField
          placeholder={outgoingOnly ? 'Vevő, számlaszám, #azonosító...' : 'Kiállító / Vevő, számlaszám, #azonosító...'}
          value={searchInput}
          onChange={v => {
            setSearchInput(v);
            setShowSuggestions(true);
            if (!v) setFilters(f => ({...f, q:''}));
          }}
          onEnter={() => {
            setFilters(f => ({...f, q:searchInput.trim()}));
            setShowSuggestions(false);
            // `#1626` → a bizonylat panelje is nyíljon meg: a panel külön kéri le,
            // így akkor is látszik, ha az aktív alapszűrő (pl. csak bejövő) kizárja.
            const docIdQ = parseDocIdQuery(searchInput);
            if (docIdQ) navigate(`/documents/${docIdQ}`);
          }}
          onEscape={() => setShowSuggestions(false)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          onClear={() => { setSearchInput(''); setFilters(f => ({...f, q:''})); }}
          style={{marginLeft:'auto', flex:'0 1 320px', minWidth:200}}>
          {showSuggestions && suggestions.length > 0 && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:200,
              background:'var(--bg-card)',border:'1px solid var(--border)',
              borderRadius:'var(--radius)',boxShadow:'0 4px 16px rgba(0,0,0,0.35)',
              marginTop:2,overflow:'hidden'}}>
              {suggestions.map((s,i) => (
                <div key={i}
                  onMouseDown={() => { setSearchInput(s); setFilters(f=>({...f,q:s})); setShowSuggestions(false); }}
                  style={{padding:'0.42rem 0.75rem',cursor:'pointer',fontSize:'0.82rem',
                    borderBottom: i<suggestions.length-1 ? '1px solid var(--border-dim)' : 'none'}}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--bg-hover)'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  {s}
                </div>
              ))}
            </div>
          )}
        </SearchField>
      </div>

      {/* ⚠ A partner-szűrés LÁTHATÓ és visszavonható. Egy néma URL-paraméteres szűkítés
          („miért csak ezeket látom?") pontosan az a fail-silent viselkedés, amit kerülünk. */}
      {partnerId && (
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:'0.75rem',
          padding:'0.45rem 0.7rem',borderRadius:'var(--radius)',flexWrap:'wrap',
          background:'var(--primary-subtle)',border:'1px solid var(--primary)',fontSize:'0.78rem'}}>
          <Users size={14} style={{color:'var(--primary)',flexShrink:0}}/>
          <span>
            Partner szerinti szűrés: <b>{filterPartner?.name || `#${partnerId}`}</b>
            <span style={{color:'var(--text-muted)'}}> · minden irány látszik (bejövő, kimenő, B.A.J.)</span>
          </span>
          <span style={{flex:1}}/>
          <Link to={`/partners/${partnerId}`} style={{color:'var(--primary)'}}>Partner adatlapja →</Link>
          <Btn size="sm" variant="outline" icon={X} onClick={clearPartnerFilter}>Szűrés törlése</Btn>
        </div>
      )}
      {bajActive && !isAdmin && (
        <Alert type="info">
          A bér-bizonylatok ebben a listában nem jelennek meg — csak cég-adminisztrátor látja őket.
        </Alert>
      )}
      {noBaseFilter ? (
        <Alert type="info">
          Válassz a Bejövő, Kimenő, vagy B.A.J. kategóriákból a bizonylatok listázásához.
        </Alert>
      ) : (<>
      {/* ── Gyorsszűrő-sor: 1 kattintásos pillek, címkézett csoportokban ── */}
      {(() => {
        const qLabel = { fontSize:'0.6rem',fontWeight:700,letterSpacing:'0.1em',
          textTransform:'uppercase',color:'var(--text-dim)',marginRight:3,userSelect:'none' };
        const qSep = { width:1,height:20,background:'var(--border)',margin:'0 6px' };
        const anyFilter = dateFilter || extraFilters.size > 0 || currencyFilters.size > 0
          || baseFilters.has('cancelled') || filters.status || filters.category
          || filters.currency || filters.q;
        const clearAll = () => {
          setDateFilter(null);
          setDateRange({ from: daysAgoYmd(7), to: todayYmd() });
          setExtraFilters(new Set());
          setCurrencyFilters(new Set());
          setFilters({ status:'', category:'', currency:'', q:'' });
          setSearchInput('');
          setBaseFilters(prev => {
            if (!prev.has('cancelled')) return prev;
            const next = new Set(prev); next.delete('cancelled'); return next;
          });
        };
        return (
        <div style={{display:'flex',gap:5,marginBottom:'0.75rem',flexWrap:'wrap',alignItems:'center'}}>
          <span style={qLabel}>Állapot</span>
          <Pill active={extraFilters.has('unpaid')}
            onClick={()=>toggleExtra('unpaid')}>Utalandó</Pill>
          <Pill active={extraFilters.has('overdue')}
            onClick={()=>toggleExtra('overdue')}>Lejárt</Pill>
          <Pill active={extraFilters.has('proforma')}
            onClick={()=>toggleExtra('proforma')}
            title="Csak proforma / előlegbekérő bizonylatok">Proforma/előleg</Pill>
          <Pill active={extraFilters.has('no-pdf')}
            onClick={()=>toggleExtra('no-pdf')}>PDF nélkül</Pill>
          <Pill active={extraFilters.has('nav-mismatch')}
            onClick={()=>toggleExtra('nav-mismatch')}>NAV≠PDF</Pill>

          <span style={qSep}/>

          <span style={qLabel}>Dátum</span>
          <select value={dateField} onChange={e=>setDateField(e.target.value)}
            title="Melyik dátum szerint szűrjenek az időszűrők"
            style={{background:'var(--bg-input)',border:'1px solid var(--border)',
              borderRadius:20,color:'var(--text)',fontSize:'0.72rem',padding:'0.26rem 0.55rem',
              cursor:'pointer',outline:'none'}}>
            <option value="delivery_date">Teljesítés</option>
            <option value="issue_date">Kelt</option>
            <option value="due_date">Fiz. határidő</option>
            <option value="paid_date">Fizetés</option>
          </select>
          <Pill active={dateFilter==='this-month'}
            onClick={()=>setDateFilter(d => d==='this-month' ? null : 'this-month')}>Akt. hónap</Pill>
          <Pill active={dateFilter==='last-month'}
            onClick={()=>setDateFilter(d => d==='last-month' ? null : 'last-month')}>Előző hónap</Pill>
          <Pill active={dateFilter==='this-year'}
            onClick={()=>setDateFilter(d => d==='this-year' ? null : 'this-year')}>Akt. év</Pill>
          <div style={{display:'inline-flex',alignItems:'center',gap:4,
            background: dateFilter==='custom' ? 'var(--primary-subtle)' : 'transparent',
            border:`1px solid ${dateFilter==='custom' ? 'var(--primary)' : 'var(--border)'}`,
            borderRadius:20,padding:'0.18rem 0.5rem'}}>
            <input type="date" value={dateRange.from}
              onChange={e=>{setDateRange(r=>({...r,from:e.target.value})); setDateFilter('custom');}}
              style={{background:'transparent',border:'none',color:'var(--text)',
                fontSize:'0.72rem',fontFamily:'var(--mono)',outline:'none',width:120}}/>
            <span style={{color:'var(--text-muted)',fontSize:'0.7rem'}}>…</span>
            <input type="date" value={dateRange.to}
              onChange={e=>{setDateRange(r=>({...r,to:e.target.value})); setDateFilter('custom');}}
              style={{background:'transparent',border:'none',color:'var(--text)',
                fontSize:'0.72rem',fontFamily:'var(--mono)',outline:'none',width:120}}/>
          </div>

          <span style={qSep}/>

          <span style={qLabel}>Deviza</span>
          {['HUF','EUR','USD'].map(cur => (
            <Pill key={cur} active={currencyFilters.has(cur)} color={currencyColor(cur)}
              onClick={()=>toggleCurrency(cur)} title={`Csak ${cur}`}
              style={{fontWeight:700, fontFamily:'var(--mono)', letterSpacing:'0.04em'}}>
              {cur}
            </Pill>
          ))}

          <span style={qSep}/>

          <Pill active={baseFilters.has('cancelled')}
            onClick={()=>toggleBaseFilter('cancelled')}
            title="Törölt (lomtár) bizonylatok megjelenítése">Törölt</Pill>

          {anyFilter && (
            <Btn variant="ghost" size="sm" icon={X} onClick={clearAll}
              style={{marginLeft:2}}>Szűrők törlése</Btn>
          )}
        </div>
        );
      })()}

      {/* Szűrők panel */}
      {showFilters && (
        <Card style={{marginBottom:'1rem'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:12}}>
            <Select label="Státusz" value={filters.status} onChange={e=>setFilters(f=>({...f,status:e.target.value}))}
              options={[{value:'',label:'Mind'},{value:'pending',label:'Fizetendő'},{value:'paid',label:'Fizetve'},{value:'overdue',label:'Lejárt'}]}/>
            <Select label="Kategória" value={filters.category} onChange={e=>setFilters(f=>({...f,category:e.target.value}))}
              options={[{value:'',label:'Mind'},...filterCats.map(c=>({value:c,label:c}))]}/>
            <Select label="Deviza" value={filters.currency} onChange={e=>setFilters(f=>({...f,currency:e.target.value}))}
              options={[{value:'',label:'Mind'},{value:'HUF',label:'HUF'},{value:'EUR',label:'EUR'}]}/>
            <div style={{display:'flex',alignItems:'flex-end'}}>
              <Btn variant="ghost" size="sm" icon={X}
                onClick={()=>setFilters({status:'',category:'',currency:'',q:''})}>Törlés</Btn>
            </div>
          </div>
        </Card>
      )}

      {/* Kijelölés sáv */}
      {selected.length > 0 && (() => {
        const selectedDocs = docs.filter(d => selected.includes(d.id));
        const incomingSelected = selectedDocs.filter(d => d.direction !== 'outgoing');
        const skipped = incomingSelected.length - payableDocs.length;
        const skippedReasons = [];
        if (skipped > 0) {
          const noIban = incomingSelected.filter(d => {
            const st = getIbanStatus(d);
            return st !== 'found' && st !== 'missing'
              && !d.payment_state?.blocks_new_payment && (d.gross_amount||0) > 0;
          }).length;
          const negAmt = incomingSelected.filter(d => (d.gross_amount||0) <= 0).length;
          // ⚠ A megszűnt `payment_draft_id` marker helyett az EGYESÍTETT állapot: így a
          //   magyarázat a Raiffeisen-utalásokra is helyes, nem csak a Revolut-draftra.
          const inPay = incomingSelected.filter(d => d.payment_state?.blocks_new_payment);
          if (noIban > 0) skippedReasons.push(`${noIban} hiányzó IBAN`);
          if (negAmt > 0) skippedReasons.push(`${negAmt} jóváírás`);
          if (inPay.length > 0) {
            const labels = [...new Set(inPay.map(d => d.payment_state?.label).filter(Boolean))];
            skippedReasons.push(`${inPay.length} ${labels.join('/') || 'utalás alatt'}`);
          }
        }
        return (
          <div style={{background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.3)',
            borderRadius:'var(--radius)',padding:'0.6rem 1rem',marginBottom:'0.75rem',
            display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
            <span style={{fontSize:'0.82rem',color:'var(--primary)'}}>{selected.length} kijelölve</span>
            <span style={{fontFamily:'var(--mono)',fontSize:'0.85rem',fontVariantNumeric:'tabular-nums'}}>
              {selectedByCurrency.map(([cur, sum]) => fmtAmt(sum, cur)).join('  +  ') || '–'}
            </span>
            {skipped > 0 && (
              <span style={{fontSize:'0.72rem',color:'#f59e0b'}}
                title={skippedReasons.join(', ')}>
                ⚠ {skipped} nem utalható{skippedReasons.length > 0 ? ` (${skippedReasons.join(', ')})` : ''}
              </span>
            )}
            <div style={{marginLeft:'auto',display:'flex',gap:8}}>
              {/* Q2: tömeges státusz (Gábor 2026-09-05: „kell tömeges beállítási lehetőség") */}
              <select value="" onChange={e=>{ if (e.target.value) bulkSetStatus(e.target.value); }}
                disabled={bulkStatusMut.isPending}
                style={{fontSize:'0.78rem',padding:'0.35rem 0.5rem',borderRadius:'var(--radius)',
                  border:'1px solid var(--border)',background:'var(--bg-surface)',color:'var(--text)'}}
                title="A kijelöltek státuszának tömeges beállítása">
                <option value="">Státusz…</option>
                <option value="paid">Fizetve (rendezett)</option>
                <option value="pending">Fizetendő</option>
                <option value="cancelled">Törölt</option>
              </select>
              {payableDocs.length > 0 && (
                <Btn size="sm" variant="secondary" icon={CreditCard}
                  onClick={()=>setPayModal({docs:payableDocs})}>
                  Utalás ({payableDocs.length})
                </Btn>
              )}
              <Btn size="sm" variant="outline"
                icon={acctGreen ? CheckCircle : Send}
                loading={accountantMut.isPending}
                style={acctGreen ? {background:'rgba(16,185,129,0.15)',
                  borderColor:'var(--accent)',color:'var(--accent)'} : undefined}
                onClick={()=>{ setAcctResult(null); setShowAcctModal(true); }}>
                {acctGreen ? 'Elküldve' : 'Könyvelőnek küld'}
              </Btn>
              <Btn size="sm" variant="ghost" onClick={()=>setSelected([])}>✕</Btn>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog open={!!bulkConfirm} title={bulkConfirm?.title} message={bulkConfirm?.message}
        danger={bulkConfirm?.danger} confirmLabel={bulkConfirm?.confirmLabel}
        onCancel={()=>setBulkConfirm(null)}
        onConfirm={()=>{ const a=bulkConfirm.action; setBulkConfirm(null); a(); }}/>

      {/* Táblázat */}
      {docsPage && (
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
          <Pagination
            mode={docsPage.mode} page={docsPage.page} totalPages={docsPage.total_pages}
            pageLabel={docsPage.page_label}
            onPageChange={setPage} viewMode={viewMode} onViewModeChange={handleViewModeChange}
            showViewSelector/>
        </div>
      )}
      <Card noPad>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8rem'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                <th style={{...thStyle(null),width:36}}>
                  <input type="checkbox" style={{width:14,height:14,accentColor:'var(--primary)'}}
                    onChange={e=>setSelected(e.target.checked?docs.map(d=>d.id):[])}
                    checked={selected.length===docs.length&&docs.length>0}/>
                </th>
                <th style={thStyle(outgoingOnly?'customer_name':'supplier_name')}
                  onClick={()=>handleSort(outgoingOnly?'customer_name':'supplier_name')}>
                  <span style={{display:'flex',alignItems:'center',gap:4}}>
                    {outgoingOnly ? 'Vevő' : incomingOnly ? 'Kiállító' : 'Partner'}
                    <SortIcon field={outgoingOnly?'customer_name':'supplier_name'}/>
                  </span>
                </th>
                <th style={thStyle('issue_date')} onClick={()=>handleSort('issue_date')}>
                  <span style={{display:'flex',alignItems:'center',gap:4}}>Kelt <SortIcon field="issue_date"/></span>
                </th>
                <th style={thStyle('delivery_date')} onClick={()=>handleSort('delivery_date')}>
                  <span style={{display:'flex',alignItems:'center',gap:4}}>Teljesítés <SortIcon field="delivery_date"/></span>
                </th>
                <th style={thStyle('due_date')} onClick={()=>handleSort('due_date')}>
                  <span style={{display:'flex',alignItems:'center',gap:4}}>Határidő <SortIcon field="due_date"/></span>
                </th>
                <th style={thStyle('paid_date')} onClick={()=>handleSort('paid_date')}>
                  <span style={{display:'flex',alignItems:'center',gap:4}}>Fizetve <SortIcon field="paid_date"/></span>
                </th>
                <th style={{...thStyle('gross_amount'),textAlign:'right'}} onClick={()=>handleSort('gross_amount')}>
                  <span style={{display:'flex',alignItems:'center',gap:4,justifyContent:'flex-end'}}>Bruttó <SortIcon field="gross_amount"/></span>
                </th>
                <th style={thStyle(null)}>Kategória</th>
                <th style={thStyle(null)} title="Könyvelő / PDF">–</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={9} style={{...tdS,textAlign:'center',padding:'2rem'}}><Spinner/></td></tr>
              ) : docsError ? (
                <tr><td colSpan={9} style={{...tdS,padding:'1rem'}}>
                  <Alert type="danger" style={{marginBottom:0,display:'flex',alignItems:'center',
                    justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                    <span>A bizonylatok betöltése sikertelen.</span>
                    <Btn size="sm" variant="secondary" onClick={()=>refetchDocs()}>Újra</Btn>
                  </Alert>
                </td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan={9} style={{...tdS,textAlign:'center',color:'var(--text-muted)',padding:'2rem'}}>
                  Nincs bizonylat.
                </td></tr>
              ) : docs.map(d => (
                <DocumentRow key={d.id} doc={d}
                  ibanStatus={d.direction !== 'outgoing' ? getIbanStatus(d) : null}
                  isPayable={canPay(d)}
                  isSelected={selected.includes(d.id)}
                  anySelected={selected.length > 0}
                  onToggle={handleToggleRow}
                  onNavigate={handleNavigateRow}
                  onPay={handlePayRow}/>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {docsPage && (
        <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
          <Pagination
            mode={docsPage.mode} page={docsPage.page} totalPages={docsPage.total_pages}
            pageLabel={docsPage.page_label}
            onPageChange={setPage} viewMode={viewMode} onViewModeChange={handleViewModeChange}/>
        </div>
      )}
      </>)}

      {/* Szinkronizálás modal (egy gomb → minden szinkron-forrás) */}
      <SyncModal open={showSyncModal} onClose={()=>setShowSyncModal(false)} />

      {/* Könyvelőnek küldés modal – szöveg + állapot + elrejtés (háttérben fut tovább) */}
      <Modal open={showAcctModal} onClose={()=>setShowAcctModal(false)}
        title={`Küldés könyvelőnek (${selected.length} bizonylat)`}>
        <label style={{fontSize:'0.72rem',fontWeight:600,color:'var(--text-muted)',
          textTransform:'uppercase',letterSpacing:'0.05em'}}>Levél szövege</label>
        <textarea value={acctBody} onChange={e=>setAcctBody(e.target.value)} rows={8}
          disabled={accountantMut.isPending}
          style={{width:'100%',marginTop:6,marginBottom:'0.9rem',background:'var(--bg-input)',
            border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'0.6rem 0.7rem',
            color:'var(--text)',fontSize:'0.85rem',fontFamily:'inherit',resize:'vertical'}}/>
        {accountantMut.isPending && (
          <Alert type="info" style={{marginBottom:'0.75rem'}}>
            Küldés folyamatban… bezárhatod (Elrejtés), a háttérben tovább fut.
          </Alert>
        )}
        {acctResult?.ok && (
          <Alert type="success" style={{marginBottom:'0.75rem'}}>
            Elküldve ✓ — {acctResult.attached} csatolmány, {acctResult.sent} bizonylat → {acctResult.to}
          </Alert>
        )}
        {acctResult?.error && (
          <Alert type="danger" style={{marginBottom:'0.75rem'}}>Hiba: {acctResult.error}</Alert>
        )}
        <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
          <Btn variant="ghost" onClick={()=>setShowAcctModal(false)}>
            {accountantMut.isPending ? 'Elrejtés' : 'Bezárás'}
          </Btn>
          {!acctResult?.ok && (
            <Btn icon={Send} loading={accountantMut.isPending}
              disabled={accountantMut.isPending || !acctBody.trim()}
              onClick={()=>{ setAcctResult(null); accountantMut.mutate(acctBody); }}>
              Küldés
            </Btn>
          )}
        </div>
      </Modal>

      {/* Fizetési modal */}
      {payModal && (
        <PaymentModal
          docs={payModal.docs}
          open={!!payModal}
          onClose={()=>setPayModal(null)}
          onSuccess={()=>{
            setPayModal(null); setSelected([]);
            qc.invalidateQueries({ queryKey: ['documents'] });
            // ⚠ 2026-08-25: a részletnézet (DocSlideOver) saját ['document', id]
            // cache-e enélkül a fizetés UTÁN is a régi payment_state-et
            // mutatná - lásd a #249-es élő hibát (a modal "Utal" gombja nem
            // tűnt el, mert a slide-over sosem értesült a sikeres/elindult
            // utalásról).
            qc.invalidateQueries({ queryKey: ['document'] });
            qc.invalidateQueries({ queryKey: ['revolut-counterparties'] });
          }}
        />
      )}

      {/* Bizonylat részlet-panel (route-olt slide-over, M365-minta) */}
      <DocSlideOver doc={docId ? panelDoc : null} onClose={closePanel}
        onSave={(data)=>updateMut.mutate({id:panelDoc.id,data})}
        loading={updateMut.isPending}
        payable={panelDoc ? canPay(panelDoc) : false}
        onPay={()=>panelDoc && setPayModal({docs:[panelDoc]})}
        onNav={(dir)=>{
          const idx = docs.findIndex(x => String(x.id) === String(docId));
          const next = docs[idx + dir];
          if (next) navigate(`/documents/${next.id}`, { replace:true });
        }}/>
    </div>
  );
}


// ── Fizetési modal ─────────────────────────────────────────────────────────

// Az IBAN-kapu (backend `iban_guard.assert_known_iban[s]`) 409-es hibakódjai. Mind a
// három strukturált `detail`-t ad (`document_id`, `iban`, `supplier_name`, `message`),
// és kötegnél (A06) `detail.items`-ben MINDEN kifogásolt tételt. A kód a súlyosságot is
// hordozza: az `iban_ellentmondas` (a cégjegyzék szerint NEM a szállítóé) piros, a
// másik kettő sárga — a kettő nem ugyanaz a kockázat.
const IBAN_GATE_CODES = new Set(['ismeretlen_iban', 'iban_ellentmondas', 'penzugyi_kepviselo']);
const normIban = (v) => String(v || '').replace(/\s/g, '').toUpperCase();
// Egy megerősítés MINDIG egy (bizonylat, IBAN) párra szól (A06) — ez a pár kulcsa.
const ibanPairKey = (docId, iban) => `${docId}|${normIban(iban)}`;

const NO_TRANSFER_RIGHT_HU = 'Nincs utalási jogosultságod ennél a cégnél — kérd az admint.';

/** Utalási hiba → emberi, magyar mondat. A backend `detail`-je lehet szöveg, strukturált
 * objektum (`{error, message}`) vagy pydantic-lista — a nyers JSON soha ne jusson a
 * felhasználó elé (korábban `JSON.stringify(detail)` ment ki a sorba). */
function describePayError(e, fallback = 'Ismeretlen hiba') {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail;
  if (isStepUpRequired(e)) {
    return 'A biztonsági megerősítés lejárt — indítsd újra az utalást, és erősítsd meg passkey-jel.';
  }
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    // S2 / audit F3: a jog hiánya — a backend szövege helyett rövid, cselekvő mondat.
    if (d.error === 'nincs_utalasi_jog') return NO_TRANSFER_RIGHT_HU;
    if (d.message) return String(d.message);
    if (d.error) return String(d.error);
  }
  if (Array.isArray(d)) {
    const msgs = d.map(x => x?.msg).filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }
  if (typeof d === 'string' && d) return d;
  if (status === 502) return 'A bank nem válaszolt megbízhatóan — ellenőrizd a bankban, mielőtt újrapróbálod.';
  return e?.response?.data?.message || e?.message || fallback;
}

/** A `POST /partners/bank-accounts/confirm` hibái (routers.py `confirm_partner_bank_account`,
 * `partner_identity.confirm_bank_account`) → magyar mondat. */
function describeConfirmError(e) {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail;
  const code = d && typeof d === 'object' ? d.error : null;
  if (code === 'nincs_utalasi_jog') return NO_TRANSFER_RIGHT_HU;
  if (code === 'iban_elteres') {
    return 'Ez a számlaszám nem egyezik a bizonylaton szereplővel, ezért nem erősíthető meg. '
         + 'Ellenőrizd a bizonylat adatait.';
  }
  if (code === 'nincs_partner') {
    return (d.message || 'A bizonylat nincs partnerhez kötve.')
         + ' Addig csak erre az egy utalásra hagyhatod jóvá (lent).';
  }
  if (status === 404) return 'A bizonylat nem található (lehet, hogy közben törölték).';
  return describePayError(e, 'A megerősítés nem sikerült.');
}

function PaymentModal({ docs, open, onClose, onSuccess }) {
  const posthog = usePostHog();
  const qc = useQueryClient();
  const { requireFreshStepUp, requirePaymentStepUp, setPaymentInFlight } = useStepUp();
  const [sourceAccountId, setSourceAccountId] = useState('');
  const [sourceByCurrency, setSourceByCurrency] = useState({});   // vegyes deviza: devizánként forrásszámla
  const [reference, setReference] = useState(docs.length===1 ? (docs[0].invoice_number||'') : '');
  const [results, setResults] = useState({});
  const [isPaying, setIsPaying] = useState(false);
  const [allDone, setAllDone] = useState(false);
  // AI-IBAN kapu (REVIEW_BACKLOG 1.4) — 2026-09-24 óta ELŐRE tudjuk (preflight, lent), mi
  // hiányzik. Ha az utaláskori kapu mégis 409-cel áll meg (versenyhelyzet, közben módosult
  // bizonylat), a kifogás ide kerül, és a tétel preflight-adatát FELÜLÍRJA — a modal így a
  // megfelelő előzetes állapotba lép vissza („Számlaszám megerősítése" / eseti jóváhagyás),
  // nem egy „Utalás újraküldése" zsákutcába. `{[doc_id]: preflight-alakú elem}`.
  const [gateOverride, setGateOverride] = useState({});
  // ⚠⚠ S1 + A06 (2026-09-24): a megerősítés PÁRONKÉNT él, globális pipa NINCS.
  //   * `pairConfirm[kulcs]` — TARTÓS megerősítés (passkey → `POST /partners/bank-accounts/confirm`):
  //     `{state: 'done'|'error', msg}`. Sikeres után a számlaszám a partner-törzsben
  //     megbízható (`trust_source='passkey'`), az utaláshoz nem kell jelző.
  //   * `oneTimeOk[kulcs]` — ESETI jóváhagyás (csak erre az utalásra): a pár a kérés
  //     `confirmed_ibans`-ába (kötegen) vagy a tétel `first_payment_confirm`-jébe
  //     (egytételes kérésen) megy. Ez az út marad a tartósan nem megerősíthető
  //     tételeknek (partner nélküli bizonylat, pénzügyi képviselő).
  const [pairConfirm, setPairConfirm] = useState({});
  const [oneTimeOk, setOneTimeOk] = useState({});
  // K8/b (2026-09-23): a bér-bizonylaton a bérszámfejtő jegyzékének számlaszáma ELTÉR a
  // munkavállalói törzsben rögzítettől. A backend nem blokkol és nem javít (melyik a helyes,
  // azt ember dönti el), a jelet a bizonylat `notes`-ába írja (`email_processor.py`
  // `BER_IBAN_ELTERES_JEL`) — strukturált mező nincs rá, ezért a szövegből ismerjük fel.
  const payrollIbanMismatch = (d) => String(d?.notes || '').includes('BÉR-IBAN ELTÉRÉS');
  const payrollMismatchDocs = docs.filter(payrollIbanMismatch);
  // A „Partner felvétele" / „Számlaszám megerősítése" lépés fut (passkey + kérések).
  const [stepBusy, setStepBusy] = useState(false);
  // A lépés vagy egy elakadt utalás magyar nyelvű magyarázata a gombok felett.
  const [flowNotice, setFlowNotice] = useState(null);
  // „Partner felvétele": a tulajdonos által szerkesztett Revolut-partner adatok IBAN-onként
  // (`{[iban]: {name, street_line1, street_line2, postcode, city, region, country}}`), és a
  // Revolut elutasításának magyar mondata ugyanígy.
  const [cpDrafts, setCpDrafts] = useState({});
  const [cpErrors, setCpErrors] = useState({});
  // Az eseti jelző egy KONKRÉT tételre: igaz, ha a felhasználó épp ezt a párt hagyta jóvá.
  const oneTimeFor = (doc) => !!oneTimeOk[ibanPairKey(doc.id, doc.bank_account_iban)];

  // Az első (vagy egyetlen) számla devizája = elvárt terhelési számla devizaneme
  const targetCurrency = docs[0]?.currency || 'HUF';
  const mixedCurrencies = docs.some(d => (d.currency||'HUF') !== targetCurrency);
  const currencies = [...new Set(docs.map(d => d.currency || 'HUF'))];   // batch-ben előforduló devizák

  // ⚠⚠ A KORÁBBI külön `revolut-accounts` query MEGSZŰNT (2026-08-12). A modal
  // mindkét bank forrásait a `/payments/sources`-ból kapja, mert CSAK ott van rajtuk
  // az `eligible` döntés. Amíg a Revolut-ág a saját, bank-specifikus végpontjáról
  // jött, a felület maga próbálta kitalálni, mi utalható — és csak DEVIZÁRA tudott
  // szűrni, célszámla-országra nem. Ez volt az `⚠ Nincs {deviza} devizájú forrás`
  // figyelmeztetés gyökere, amit Gábor kifogásolt: figyelmeztetni kellett, mert a
  // felület olyat ajánlott fel, amit a bank elutasít.

  const { data: appSettings = {} } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
    enabled: open,
    staleTime: 60_000,
  });
  // M8 (2026-09-05): a draft-mód a REVOLUT-KAPCSOLAT beállítása (company_assets.settings),
  // és FORRÁSONKÉNT jön a `/payments/sources`-ból (`use_drafts`) — a régi cég-szintű
  // `revolut_use_drafts` Settinget a felület már nem olvassa. Lásd lentebb `useDrafts`.
  // ⚠ A cég adószáma a `companies` táblából (companyApi), NEM a settings-ből: a
  // `company_tax_reference` settings-kulcs 2026-08-12-én megszűnt. Ez az érték az
  // adó-közlemény eltérés-ellenőrzés bemenete — ha üresen maradna, a figyelmeztetés
  // CSENDBEN sosem jelenne meg (a mismatch-lista üres tax-referencián kiürül).
  const { data: companyInfo = {} } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
    enabled: open,
    staleTime: 60_000,
  });
  const companyTaxReference = (companyInfo.tax_reference_number || '').trim();

  // Több-bankos források — MINDKÉT bank a `/payments/sources`-ból, a bizonylatokra
  // szűrve. A backend a connectorok képességei szerint dönt (deviza ÉS célszámla
  // országa), és a nem alkalmas számlákra `requirement` szöveget ad.
  // ⚠ A `document_id` a queryKey része: más bizonylat-halmaz MÁS válasz, tehát nem
  // oszthatják meg a cache-t. Vesszős lista, mert a köteg vegyes devizájú lehet.
  const payDocIds = docs.map(d => d.id).filter(Boolean).join(',');
  const { data: paySources = [], isFetched: paySourcesFetched } = useQuery({
    queryKey: ['payment-sources', payDocIds],
    queryFn: () => paymentsApi.sources(payDocIds ? { document_id: payDocIds } : undefined),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });

  // ── ELŐZETES ELLENŐRZÉS (2026-09-24, tulajdonosi kérés) ─────────────────────────────
  // „A rendszer pontosan tudja, hogy létezik-e már ez a partner revoluton." — ezért az alsó
  // gomb NEM egy utólagos 409-ből tanulja meg, mi hiányzik, hanem előre:
  //   (a) a Revolut-counterparty létezése: az élő `GET /revolut/counterparties` lista
  //       (ugyanaz a cache-bejegyzés, mint a Bizonylatok lapé — a felvétel után frissül);
  //   (b) a számlaszám megbízhatósága: `POST /payments/preflight` — a backend UGYANAZZAL a
  //       függvénnyel dönt, mint az utaláskori IBAN-kapu (`iban_guard.iban_preflight`).
  // A 409-es utólagos kapu védelemként megmarad (`gateOverride`).
  const { data: revolutCps = [], isFetched: cpsFetched, isError: cpsError } = useQuery({
    queryKey: ['revolut-counterparties'],
    queryFn: revolutApi.counterparties,
    enabled: open,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });
  const cpByIban = useMemo(() => {
    const map = new Map();
    revolutCps.forEach(cp => (cp.accounts || []).forEach(acc => {
      const ib = normIban(acc.iban);
      if (ib) map.set(ib, cp);
    }));
    return map;
  }, [revolutCps]);
  const payDocIdList = docs.map(d => d.id).filter(Boolean);
  const { data: preflight, isFetched: preflightFetched, isError: preflightError,
          refetch: refetchPreflight } = useQuery({
    queryKey: ['payment-preflight', payDocIds],
    queryFn: () => paymentsApi.preflight(payDocIdList),
    enabled: open && payDocIdList.length > 0,
    staleTime: 0,
    retry: false,
  });
  const preflightByDoc = useMemo(() => Object.fromEntries(
    (preflight?.items || []).map(i => [i.document_id, i])), [preflight]);

  // Raiffeisen-egyenlegek: KÜLÖN, nem blokkoló kérés (a /sources-nak nem része, mert
  // az külső banki hívás lenne — lásd a végpont docstringjét). A modal a lista
  // megjelenítésére NEM vár: az egyenlegek utólag ülnek a helyükre, és ha a bank nem
  // válaszol, a forrás egyszerűen egyenleg nélkül látszik.
  const { data: raifBalances } = useQuery({
    queryKey: ['payment-balances'],
    queryFn: paymentsApi.balances,
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });
  const raifBalanceByIban = {};
  for (const b of (raifBalances?.balances || [])) {
    if (b.iban) raifBalanceByIban[b.iban.replace(/\s/g, '').toUpperCase()] = b;
  }
  // ── „Utalás ideje" (T / T-1 / T-2 / T-1M / T-2M) ────────────────────────
  // A -M szabályok magyar BANKI naptáron számolnak, ezért a dátumokat a backend adja.
  // Több bizonylatnál a LEGKORÁBBI lejáratra kérjük a címkéket (a tényleges dátumot
  // a backend tételenként, az adott bizonylat saját lejáratából számolja).
  // Alapértelmezés: „Most" (azonnali, dátum nélkül) — ez a mai, bevált viselkedés,
  // tehát a legördülő megjelenése senkinek nem változtatja meg az utalásait.
  const [scheduleRule, setScheduleRule] = useState('MOST');
  const earliestDue = docs.map(d => d.due_date).filter(Boolean).sort()[0] || null;
  const { data: schedule } = useQuery({
    queryKey: ['schedule-options', earliestDue],
    queryFn: () => paymentsApi.scheduleOptions(earliestDue),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });
  const schedOpts = schedule?.options || [];
  const schedSel = schedOpts.find(o => o.rule === scheduleRule);
  const schedApprox = (schedule?.calendar || []).length > 0;

  const raifSources = paySources
    .filter(s => s.bank === 'raiffeisen' && s.iban)
    .map(s => {
      const bal = raifBalanceByIban[s.iban.replace(/\s/g, '').toUpperCase()];
      // (az `eligible` / `requirement` / `eligible_for` a backend döntése — lásd lentebb)
      // `available` (ITAV) az elsődleges – ebből lehet utalni; ha a bank csak könyvelt
      // egyenleget adott (ITBD), az a tartalék.
      // ⚠ A bank STRINGET ad ("1234567800.000"), a formázó viszont számot vár – itt
      // konvertálunk. Érvénytelen/hiányzó érték → null, és a dropdown ilyenkor csak a
      // devizát írja ki (pontosan úgy, mint egyenleg-adat nélkül eddig).
      const rawBal = bal ? (bal.available ?? bal.balance ?? bal.current) : null;
      const numBal = rawBal == null || rawBal === '' ? null : Number(rawBal);
      return {
        id: 'raif:' + s.iban, iban: s.iban, bank: 'raiffeisen',
        currency: s.currency || 'HUF',
        balance: Number.isFinite(numBal) ? numBal : null,
        name: s.label || ('Raiffeisen …' + s.iban.slice(-6)),
        eligible: s.eligible !== false, requirement: s.requirement,
        eligible_for: s.eligible_for,
        supports_revocable_draft: s.supports_revocable_draft,
        company_asset_id: s.company_asset_id ?? null,
      };
    });
  // ⚠ A Revolut-forrás ugyanabból a `/payments/sources` válaszból jön, mint a
  //   Raiffeisen — az `account_id` → `id`, a `label` → `name` a modal belső alakja.
  const revSources = paySources
    .filter(s => s.bank === 'revolut' && s.account_id)
    .map(s => ({
      id: s.account_id, bank: 'revolut', currency: s.currency,
      name: s.label || s.currency || 'Revolut',
      balance: s.balance == null ? null : Number(s.balance),
      eligible: s.eligible !== false, requirement: s.requirement,
      eligible_for: s.eligible_for,
      supports_revocable_draft: s.supports_revocable_draft,
      // M8: a Revolut-kapcsolat asset-id-je + az EFFEKTÍV draft-mód erre a forrásra.
      company_asset_id: s.company_asset_id ?? null,
      use_drafts: s.use_drafts,
    }));
  const allSources = [...revSources, ...raifSources];
  // ⚠ Draft-mód: a Revolut-források `use_drafts`-a dönt (asset settings → backend
  //   tartalék). Ha bármelyik érintett Revolut-forrás azonnali (`false`), a köteg
  //   azonnali. Hiányzó mező (`undefined`, régebbi backend) → draft, a biztonságos irány:
  //   a `false→true` tévedés csak egy fölösleges „DRAFT" felirat, a fordítottja hazugság
  //   volna elindult pénzről — ezért itt `!== false`, nem `=== true`.
  const useDrafts = !revSources.some(s => s.use_drafts === false);

  // ⚠⚠ ITT DŐL EL GÁBOR ELVE: „nem állhat elő olyan helyzet, hogy a rendszernek
  //   figyelmeztetnie kell, hogy ez a számla ezen a bankon nem utalható."
  //   Nem devizára szűrünk (az csak a fél igazság volt), hanem a BACKEND
  //   képesség-döntésére. És NINCS „ha semmi nem egyezik, mutasd az összeset"
  //   tartalék: korábban az volt itt, és épp az ajánlott fel olyan számlát, amit a
  //   bank elutasít — utána kellett figyelmeztetni. Ha semmi nem alkalmas, a lista
  //   ÜRES, és megmondjuk, MI HIÁNYZIK (`missingRequirements`).
  const eligibleSources = allSources.filter(a => a.eligible);
  const displayAccounts = eligibleSources;
  // A dropdown fejlécében a deviza csak akkor jelenik meg, ha van is mit választani.
  const matchingAccounts = eligibleSources;

  // „Mi hiányzik" — sorrend-tartó duplikátum-szűréssel (a backend `requirement`-jei).
  const missingRequirements = [...new Set(
    allSources.filter(a => !a.eligible && a.requirement).map(a => a.requirement))];

  // "Utalás alapbeállításai" – devizánkénti default forrás (bank-agnosztikus:
  // Revolut account_id vagy Raiffeisen iban).
  let defaultSources = {};
  try { defaultSources = JSON.parse(appSettings.payment_default_sources || '{}') || {}; } catch { defaultSources = {}; }
  const defaultSourceId = (ccy) => {
    const d = defaultSources[ccy];
    if (!d || !d.bank) return null;
    if (d.bank === 'revolut' && d.account_id && allSources.some(a => a.id === d.account_id)) return d.account_id;
    if (d.bank === 'raiffeisen' && d.account && allSources.some(a => a.id === 'raif:' + d.account)) return 'raif:' + d.account;
    return null;
  };

  // ⚠ Egy forrás akkor jó EGY bizonylatra, ha a backend arra a bizonylatra
  //   alkalmasnak jelölte. Az `eligible_for` bizonylatonkénti térkép azért kell, mert
  //   egy vegyes köteg tételei eltérő devizájúak ÉS eltérő célszámla-országúak
  //   lehetnek — a köteg-szintű `eligible` (mindegyikre jó) ilyenkor túl szigorú.
  const okForDoc = (a, docId) =>
    a.eligible_for ? a.eligible_for[String(docId)] === true : a.eligible;
  //: Egy devizához azok a források, amik a köteg MINDEN azon devizájú tételét elviszik.
  const sourcesForCurrency = (cur) => {
    const ids = docs.filter(d => (d.currency || 'HUF') === cur).map(d => d.id);
    return allSources.filter(a => ids.every(id => okForDoc(a, id)));
  };

  // Auto-select: a beállított default forrás (ha van), különben az első ALKALMAS.
  // ⚠ Csak alkalmas forrást választunk ki magától — korábban `allSources[0]` volt a
  //   tartalék, ami egy nem utalható számlát is beállíthatott.
  useEffect(() => {
    if (eligibleSources.length > 0) {
      const dflt = defaultSourceId(targetCurrency);
      const ok = dflt && eligibleSources.some(a => a.id === dflt) ? dflt : null;
      setSourceAccountId(ok || eligibleSources[0].id);
    } else {
      setSourceAccountId('');
    }
  }, [paySources, targetCurrency, appSettings.payment_default_sources]);

  // Vegyes deviza: devizánként default forrásszámla (csak ALKALMAS forrásból).
  useEffect(() => {
    if (allSources.length === 0) return;
    setSourceByCurrency(prev => {
      const next = { ...prev };
      for (const cur of new Set(docs.map(d => d.currency || 'HUF'))) {
        const opts = sourcesForCurrency(cur);
        if (!next[cur] || !opts.some(a => a.id === next[cur])) {
          const dflt = defaultSourceId(cur);
          const ok = dflt && opts.some(a => a.id === dflt) ? dflt : null;
          next[cur] = ok || opts[0]?.id || '';
        }
      }
      return next;
    });
  }, [paySources, appSettings.payment_default_sources]);

  // Batch esetén minden számla a saját devizájának megfelelő forrást kapja
  // (vegyes devizánál a devizánként kiválasztott forrás).
  const findAccountForDoc = (doc) => {
    const cur = doc.currency || 'HUF';
    if (mixedCurrencies && sourceByCurrency[cur]) return sourceByCurrency[cur];
    const match = allSources.find(a => okForDoc(a, doc.id));
    return (match || allSources.find(a => a.id === sourceAccountId))?.id || sourceAccountId;
  };

  // A dokumentumhoz kiválasztott forrás OBJEKTUMA (bank-elágazáshoz).
  const sourceObjFor = (doc) => {
    const id = mixedCurrencies ? findAccountForDoc(doc) : sourceAccountId;
    return allSources.find(s => s.id === id);
  };
  const selectedSource = allSources.find(s => s.id === sourceAccountId);
  const selectedBank = selectedSource?.bank;

  // ⚠ Nem minden bank ismeri a visszavonható DRAFT-ot: a Raiffeisennél a consent MAGA a
  // jóváhagyás, tehát az utalás azonnal elmegy. A feliratok ezért NEM a `useDrafts`
  // beállításon állnak, hanem azon, hogy a művelet TÉNYLEGESEN draftot hoz-e létre.
  // Az `isRaif` ehhez nem elég: az csak az egy-forrásos esetet nézi, vegyes devizás
  // kötegnél viszont az azonnali forrás bujkálhat a devizánkénti választásban.
  //
  // ⚠⚠ A DÖNTÉST A BACKEND KÉPESSÉG-FLAGJE ADJA (`supports_revocable_draft`), NEM
  // banknév-hasonlítás. Korábban itt `s.bank === 'raiffeisen'` állt; egy ÚJ bank
  // connectora némán a „draft készül" ágra esett volna, mert a felület nem tud róla.
  // A flag `false`-ra default a `PaymentProvider`-ben (fail-closed), tehát a rosszabbik
  // eset egy fölöslegesen óvatos felirat, nem egy hazugság elindult pénzről.
  // Backend: `services/payment/base.py::PaymentProvider.supports_revocable_draft`.
  //
  // ⚠ A `?? false` NEM stiláris: egy régebbi backend (vagy egy hibázó forrás-hívás)
  // `undefined`-ot adna, amitől a `!anyImmediateBank` igazzá válna — vagyis pont a
  // veszélyes irányba dőlnénk. Hiányzó flag = nincs draft.
  //
  // ⚠⚠ HÁROM ÁLLAPOT, NEM KETTŐ. A „még nincs kiválasztva forrás" NEM ugyanaz, mint
  // az „azonnal elmegy" — a modal nyitáskor üres forrás-választóval indul. Ha a
  // kettőt összevonnánk, a felhasználó piros „végleges utalás" figyelmeztetést kapna,
  // mielőtt bármit választott volna; ha pedig a másik irányba vonnánk össze, DRAFT-ot
  // ígérnénk ismeretlen forrásra. Ezért `unknown` a harmadik állapot: semleges
  // felirat, semmilyen ígéret. (Az „Utal" gomb ilyenkor amúgy is tiltva, `payDisabled`.)
  const docSources = docs.map(d => sourceObjFor(d));
  const anyUnresolvedSource = docSources.some(s => !s);
  const anyImmediateBank = docSources.some(
    s => s && !(s.supports_revocable_draft ?? false));
  //: 'draft' | 'immediate' | 'unknown'
  const payMode = anyUnresolvedSource ? 'unknown'
    : (useDrafts && !anyImmediateBank) ? 'draft' : 'immediate';
  const willCreateDrafts = payMode === 'draft';
  //: A ténylegesen érintett bankok — az analitikai címkéhez (vegyes köteg is lehet).
  const banksUsed = [...new Set(docSources.filter(Boolean).map(s => s.bank))];

  // Utalási közlemény (reference) építése egy dokumentumhoz.
  //  - 1 db doc + kézzel beírt reference  → azt használjuk
  //  - TAX típus: notes "AI közlemény: ..." rész, normalizálva (placeholder → company tax ref)
  //  - egyéb: invoice_number
  //  - mindkettő üres → supplier_name alapú fallback (Revolut nem fogad el üreset)
  const buildReference = (doc) => {
    const manual = (reference || '').trim();
    if (manual && docs.length === 1) return manual.slice(0, 140);

    let ref = '';
    const dtype = (doc.doc_type || '').toLowerCase();
    if (dtype === 'tax') {
      const notes = doc.notes || '';
      const m = notes.match(/AI\s*k[öo]zlem[ée]ny:\s*(.+?)(?:\s*\||\s*$)/i);
      const aiRef = (m ? m[1] : '').trim();
      const low = aiRef.toLowerCase();
      const placeholders = ['adószám','adoszam','közlemény adószám','kozlemeny adoszam','tax_number','tax number'];
      if (!aiRef || placeholders.includes(low)) {
        ref = companyTaxReference;
      } else if (companyTaxReference && low.includes('adószám') && !aiRef.includes(companyTaxReference)) {
        ref = `${aiRef} ${companyTaxReference}`;
      } else {
        ref = aiRef;
      }
    } else {
      ref = (doc.invoice_number || '').trim();
    }

    if (!ref) {
      // Végső fallback — Revolut sosem fogad üres referenciát.
      ref = doc.supplier_name
        ? `PhenoFin – ${doc.supplier_name}`
        : 'PhenoFin utalás';
    }
    return ref.slice(0, 140);
  };

  /* ADÓ-KÖZLEMÉNY ELTÉRÉS — megerősítést igényel (Gábor, 2026-08-05).
   *
   * Adó (TAX) típusú tételnél az ALAPÉRTELMEZÉS a tenant cég adószáma: a NAV azon
   * azonosítja be a befizetést. Ha a könyvelő ettől ELTÉRŐ közleményt javasolt (az AI
   * a levélből ezt olvasta ki), az lehet teljesen jogos (pl. más adónem hivatkozása),
   * de lehet elgépelés is — és egy rossz közleményű adóbefizetés a NAV-nál nem
   * párosul, azaz úgy viselkedik, mintha be sem fizettük volna.
   *
   * Ezért NEM írjuk felül a könyvelő javaslatát, és nem is fogadjuk el csendben:
   * megmutatjuk, és a felhasználónak meg kell erősítenie. */
  const taxRefMismatch = (doc) => {
    if ((doc.doc_type || '').toLowerCase() !== 'tax') return null;
    if (!companyTaxReference) return null;
    const ref = buildReference(doc);
    if (ref.includes(companyTaxReference)) return null;
    return { ref, expected: companyTaxReference };
  };
  const mismatchDocs = docs.filter(d => taxRefMismatch(d));
  const [taxRefConfirmed, setTaxRefConfirmed] = useState(false);

  // ⚠⚠ A Revolut-partner adatai (2026-09-24, élő hiba: „postcode is empty"). A cím
  // országfüggő bontása a `utils/addressParse.js`-ben van (gyártói szabály + forrás a fájl
  // fejében); HU szállítónál a NAV strukturált címe (`supplier_address_nav`) az első forrás.
  // A felismert értékek a modalban SZERKESZTHETŐK („Partner felvétele" előtt) — a tulajdonos
  // kezeskedik a partnerért, tehát lássa, mi megy a Revolutnak. A passkey-szándék a
  // VÉGLEGES, szerkesztett törzsre szól (`counterpartyBody` a kattintáskor olvassa a
  // mezőket; a lépés közben a mezők tiltottak).
  const defaultCpDraft = (doc) => {
    const country = normIban(doc.bank_account_iban).slice(0, 2);
    const a = parseCounterpartyAddress(doc.supplier_address,
      { ibanCountry: country, navAddress: doc.supplier_address_nav || null });
    return { name: doc.supplier_name || '', street_line1: a.street_line1,
             street_line2: a.street_line2, postcode: a.postcode, city: a.city,
             region: a.region || '', country: a.country, source: a.source };
  };
  const cpDraftFor = (doc) => cpDrafts[normIban(doc.bank_account_iban)] || defaultCpDraft(doc);
  const setCpField = (doc, field, value) => {
    const iban = normIban(doc.bank_account_iban);
    setCpDrafts(p => ({ ...p, [iban]: { ...(p[iban] || defaultCpDraft(doc)), [field]: value } }));
    setCpErrors(p => { const n = { ...p }; delete n[iban]; return n; });
  };

  // Counterparty KERESÉSE (nem létrehozása!). Visszaadja: { cpId, cpAccountId }.
  // ⚠⚠ 2026-09-24: az utalási folyamat SOHA nem hoz létre counterpartyt csendben — az a
  //   Revolut oldalán a célszámla (a pénz útja), ezért passkey-jel, a „Partner felvétele"
  //   lépésben jön létre (`runSetupStep`). Ha utaláskor mégis hiányzik, a `handlePayAll`
  //   megáll, és a modal a „Partner felvétele" állapotba lép.
  const resolveCounterparty = (doc) => {
    const cleanIban = normIban(doc.bank_account_iban);
    if (!cleanIban) throw new Error('Nincs IBAN a számlán');
    const cp = cpByIban.get(cleanIban);
    if (!cp) {
      const e = new Error('A partner még nincs felvéve a Revoluton.');
      e.code = 'cp_missing';
      throw e;
    }
    const acct = (cp.accounts || []).find(a => normIban(a.iban) === cleanIban);
    return { cpId: cp.id, cpAccountId: acct?.id || (cp.accounts || [])[0]?.id };
  };
  // A `POST /revolut/counterparties` törzse — ez megy a passkey-szándékba is, változatlanul.
  const counterpartyBody = (doc) => {
    const cleanIban = normIban(doc.bank_account_iban);
    const draft = cpDraftFor(doc);
    const address = counterpartyAddressPayload(draft);
    return {
      name: String(draft.name || '').trim(),
      iban: cleanIban,
      bank_country: cleanIban.slice(0, 2) || 'HU',   // a BANK országa = az IBAN-é
      currency: doc.currency || 'HUF',
      ...(address && { address }),
    };
  };
  // A Revolut-partner felvételének hibája → magyar mondat a mező nevével, a nyers angol mellett
  // (`POST /revolut/counterparties`: `detail.error == "revolut_elutasitotta"`, `revolut_message`).
  const describeCpError = (e) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      if (d.error === 'revolut_elutasitotta') {
        return describeRevolutCounterpartyError(d.revolut_message) || d.message;
      }
      if (d.message) return String(d.message);
    }
    return describePayError(e, 'A partner felvétele nem sikerült.');
  };

  // A 409 törzse strukturált (`detail.error`), nem szabadszöveg — így az
  // üzenet-átfogalmazás nem töri el a felismerést.
  // A06: a `detail.items` MINDEN kifogásolt tételt hordoz (egytételes kérésen is); ha
  // hiányzik (régebbi backend), az első kifogás maga a `detail`.
  const ibanGateInfo = (e) => {
    const d = e?.response?.status === 409 ? e?.response?.data?.detail : null;
    if (!(d && typeof d === 'object' && IBAN_GATE_CODES.has(d.error))) return null;
    return (Array.isArray(d.items) && d.items.length ? d.items : [d]).map(g => ({
      doc_id: g.document_id,
      iban: normIban(g.iban),
      supplier_name: g.supplier_name,
      error: g.error,
      message: g.message,
      confirmable: !!g.confirmable && !!g.confirm_endpoint,
    }));
  };

  // ⚠ 2026-08-13: a DISZPÉCSEREN keresztül (`/payments/pay`), nem közvetlenül a
  //   `/revolut/payments`-en. A body VÁLTOZATLAN — a connector ugyanannak a
  //   függvénynek adja tovább, amit eddig közvetlenül hívtunk, tehát banki oldalon
  //   nulla viselkedés-változás. Amit ezzel MEGKAPUNK: az ütemezés-feloldás
  //   (`schedule_rule`), a capability-kapu és a SZERVER-oldali `payment_initiated`
  //   esemény — utóbbi eddig csak a Raiffeisen-ágon keletkezett, tehát a
  //   szerver-oldali utalás-adat féloldalas volt.
  //   A counterparty-feloldás SZÁNDÉKOSAN itt marad: az tiszta Revolut-tudás, de a
  //   fizetés előtti lépés, és a bank saját azonosítóit adja a body-ba.
  // ⚠⚠ F6/A03 (2026-09-24): a törzset KÜLÖN építjük és küldjük. A passkey-ceremónia
  //   ELŐTT az összes törzs elkészül, a backend azok lenyomatára ad egyszeri jegyet, és
  //   UTÁNA pontosan ugyanazok az objektumok mennek ki — egy utólag módosított törzs 403.
  const buildRevolutBody = (doc) => {
    const { cpId, cpAccountId } = resolveCounterparty(doc);
    const accountId = mixedCurrencies ? findAccountForDoc(doc) : sourceAccountId;
    return {
      bank: 'revolut',
      account_id: accountId,
      // M8: melyik Revolut-kapcsolat (asset) — a backend ebből olvassa a draft-módot.
      company_asset_id: allSources.find(s => s.id === accountId)?.company_asset_id ?? null,
      counterparty_id: cpId,
      counterparty_account_id: cpAccountId,   // receiver.account_id a Revolut API-ban
      amount: doc.payable_amount != null ? doc.payable_amount : doc.gross_amount,
      currency: doc.currency || 'HUF',
      reference: buildReference(doc),
      // ⚠ NINCS `force_direct` (2026-09-24, tulajdonosi döntés): a mező megszűnt, a backend
      //   400-zal utasítja el. Draft vagy azonnali: KIZÁRÓLAG a kapcsolat beállítása dönti el.
      // tranzakciós loghoz (a bank felé nem megy):
      document_id: doc.id,
      creditor_name: doc.supplier_name,
      creditor_iban: doc.bank_account_iban?.replace(/\s/g, '').toUpperCase(),
      account_name: allSources.find(s => s.id === accountId)?.name,
      // Eseti jóváhagyás CSAK erre a (bizonylat, IBAN) párra — egytételes kérés.
      first_payment_confirm: oneTimeFor(doc),
    };
  };
  const payOne = async (doc, body) => {
    await paymentsApi.pay(body);
    await documentsApi.update(doc.id, {
      status: 'paid',
      paid_date: new Date().toISOString().slice(0,10),
    });
  };

  // Raiffeisen: azonnali /init (a bank-agnosztikus diszpécseren keresztül). Nincs
  // counterparty/draft – a consent a tartós felhatalmazás. ACSC esetén a backend a
  // bizonylatot magától PAID-re állítja (document_id alapján).
  const buildRaiffeisenBody = (doc, src) => ({
    bank:          'raiffeisen',
    // A szabály utazik, a konkrét dátumot a backend számolja a bizonylat SAJÁT
    // lejáratából (banki naptár) — köteges utalásnál tételenként más nap jön ki.
    schedule_rule: scheduleRule,
    payment_type:  (doc.currency || 'HUF') === 'EUR' ? 'SEPA' : 'HUF',
    debtor_iban:   src.iban,
    creditor_iban: doc.bank_account_iban?.replace(/\s/g, '').toUpperCase(),
    creditor_name: doc.supplier_name || '',
    amount:        doc.payable_amount != null ? doc.payable_amount : doc.gross_amount,
    currency:      doc.currency || 'HUF',
    reference:     buildReference(doc),
    document_id:   doc.id,
    // Eseti jóváhagyás CSAK erre a (bizonylat, IBAN) párra — egytételes kérés.
    first_payment_confirm: oneTimeFor(doc),
  });
  const payOneRaiffeisen = async (body) => {
    const res = await paymentsApi.pay(body);
    // A backend a HUF utalás végállapotát megvárja (poll). RJCT = a bank elutasította
    // → hibaként jelezzük (nem „OK"), a bizonylat PENDING marad, újra utalható.
    if (res?.bank_status === 'RJCT') {
      // A9: a bank megmondja, MIÉRT — ha adott indokot, azt mutatjuk, ne induljon emberi nyomozás.
      throw new Error(res?.bank_status_reason
        ? `A bank elutasította az utalást (RJCT): ${res.bank_status_reason}`
        : 'A bank elutasította az utalást (RJCT).');
    }
  };

  // ⚠⚠ „Partner felvétele" / „Számlaszám megerősítése" (2026-09-24, tulajdonosi kérés).
  // EGY kattintás = EGY friss passkey (`requireFreshStepUp`) = az itt felsorolt kérések:
  //   * „Partner felvétele" (Revolut-forrás, nincs counterparty): a counterparty-létrehozás(ok)
  //     ÉS — ahol a számlaszám nem igazolt, de tartósan megerősíthető — a (bizonylat, IBAN)
  //     megerősítés(ek). A partner felvétele = a tulajdonos kezeskedik a számlaszámért, ezért
  //     egy passkey fedi mindkettőt (a backend egy szándékban több kérést enged, mindegyiket
  //     egyszer).
  //   * „Számlaszám megerősítése" (van partner vagy Raiffeisen-forrás): a megerősítés(ek).
  // Utalást egyik sem indít: utána a modal frissíti a preflightot és a partner-listát, és a
  // normál „Utalás" nézetre vált — az utalás a saját, külön passkeyjét kéri (tulajdonosi
  // szabály: minden utalás külön passkey).
  // ⚠ A zár SZINKRON (ref), a kattintás első sorában (`utalas-biztonsag.md` 2.d).
  const stepLockRef = useRef(false);
  const runSetupStep = async (mode, rows) => {
    if (stepLockRef.current || isPaying) return;
    stepLockRef.current = true;
    setStepBusy(true);
    setPaymentInFlight(true);   // cégváltás a lépés közben se legyen
    setFlowNotice(null);
    try {
      const confirmReqs = rows.filter(r => r.needsConfirm).map(r => ({
        r, key: ibanPairKey(r.doc.id, r.iban),
        req: freshRequest('POST /partners/bank-accounts/confirm',
                          { body: { document_id: r.doc.id, iban: r.iban } }) }));
      // Egy IBAN = egy counterparty: két bizonylat ugyanarra a számlára egy felvétel (a
      // backend a szándékban az ismétlődő kérést el is utasítaná).
      const cpReqs = [];
      if (mode === 'needs_partner') {
        const seen = new Set();
        for (const r of rows) {
          if (!r.needsCp || seen.has(r.iban)) continue;
          seen.add(r.iban);
          cpReqs.push({ r, req: freshRequest('POST /revolut/counterparties',
                                             { body: counterpartyBody(r.doc) }) });
        }
      }
      if (confirmReqs.length + cpReqs.length === 0) return;
      // A StepUp-ablak mondata: „<why> passkey-jel megerősítjük, …" — ezért „… előtt".
      const why = mode === 'needs_partner'
        ? (cpReqs.length === 1 ? 'A Revolut-partner felvétele' : `${cpReqs.length} Revolut-partner felvétele`)
          + (confirmReqs.length ? ' és a számlaszám megerősítése előtt' : ' előtt')
        : (confirmReqs.length === 1 ? 'A bankszámla megerősítése előtt' : `${confirmReqs.length} bankszámla megerősítése előtt`);
      if (!await requireFreshStepUp(why, [...confirmReqs, ...cpReqs].map(x => x.req))) return;

      const errors = [];
      const who = (r) => r.doc.supplier_name || `#${r.doc.id}`;
      // 1. A megerősítések ELŐBB: ha egy számlaszám nem erősíthető meg (pl. `iban_elteres`),
      //    arra partnert sem veszünk fel.
      const failedIbans = new Set();
      for (const { r, key, req } of confirmReqs) {
        try {
          await sendFresh(req);
          setPairConfirm(p => ({ ...p, [key]: { state: 'done' } }));
        } catch (e) {
          failedIbans.add(r.iban);
          const msg = describeConfirmError(e);
          setPairConfirm(p => ({ ...p, [key]: { state: 'error', msg } }));
          errors.push(`${who(r)}: ${msg}`);
        }
      }
      // 2. A Revolut-partnerek felvétele.
      for (const { r, req } of cpReqs) {
        if (failedIbans.has(r.iban)) continue;
        try {
          await sendFresh(req);
        } catch (e) {
          const msg = describeCpError(e);
          setCpErrors(p => ({ ...p, [r.iban]: msg }));
          errors.push(`${who(r)}: ${msg}`);
        }
      }
      // 3. Frissítés: a gomb a VALÓS állapotból dönt tovább (nem abból, amit feltételezünk).
      if (confirmReqs.length) {
        qc.invalidateQueries({ queryKey: ['partners'] });
        qc.invalidateQueries({ queryKey: ['partner'] });
      }
      await Promise.all([
        cpReqs.length ? qc.invalidateQueries({ queryKey: ['revolut-counterparties'] }) : null,
        refetchPreflight(),
      ]);
      setGateOverride({});
      if (errors.length) setFlowNotice({ tone: 'danger', text: errors.join(' · ') });
    } finally {
      stepLockRef.current = false;
      setStepBusy(false);
      setPaymentInFlight(false);
    }
  };

  const handlePayAll = async () => {
    // ⚠⚠ 2026-08-24, Gábor kérése: a letiltásnak SZINKRON, a kattintás
    // pillanatában kell történnie - ha a step-up MÁR érvényes (nincs friss
    // rákérdezés), a `requireStepUp()` await-je és az (korábban itt lentebb
    // álló) `setIsPaying(true)` között egy ablak nyílt, ahol két gyors
    // kattintás MINDKETTŐ átment volna a `payDisabled` kapun, és két
    // utalást indított volna el. Az `isPaying` ezért itt, a legelején áll be.
    if (payDisabled || isPaying) return;
    setIsPaying(true);
    // M1 (2026-09-04): a fejléc cég-váltó gombját UGYANITT, szinkron tiltjuk le
    // (`StepUpContext.paymentInFlight`) — egy cégváltás utalás közben a köteg
    // felét az egyik, felét a másik cég nevében indítaná.
    setPaymentInFlight(true);
    // ⚠ CSAK a még nem sikerült tételek. Ez az IBAN-kapu miatt lett fontos: a
    // megerősítés utáni ÚJRAKÜLDÉS különben a már elindított utalásokat is
    // újraküldené — amit a dupla-utalás kapu 409-cel elutasítana, és a
    // felhasználó egy sikeres tételnél látna hibaüzenetet.
    const payDocs = docs.filter(d => results[d.id] !== 'ok');
    setFlowNotice(null);
    // ⚠⚠ 2026-09-24: Revolut-tételnél a counterpartynak MÁR léteznie kell — az utalás nem
    //   hoz létre partnert. Ha a lista közben változott (versenyhelyzet), itt megállunk
    //   passkey nélkül, frissítjük a listát, és a modal a „Partner felvétele" állapotba lép.
    const cpMissing = payDocs.filter(d => sourceObjFor(d)?.bank === 'revolut'
      && normIban(d.bank_account_iban) && !cpByIban.has(normIban(d.bank_account_iban)));
    if (cpMissing.length) {
      qc.invalidateQueries({ queryKey: ['revolut-counterparties'] });
      setFlowNotice({ tone: 'warning', text: 'Az utalás nem indult el: '
        + (cpMissing.length === 1 ? 'a partner' : `${cpMissing.length} partner`)
        + ' még nincs felvéve a Revoluton. Előbb vedd fel (lenti gomb, passkey-jel).' });
      setIsPaying(false); setPaymentInFlight(false); return;
    }
    const init = { ...results };
    payDocs.forEach(d => { init[d.id] = 'loading'; });
    setResults(init);
    const gateHits = [];
    const clearRows = (ids) => setResults(r => {
      const n = { ...r };
      ids.forEach(id => { delete n[id]; });
      return n;
    });

    // ── 1. TERV: minden banki kérés törzse elkészül a passkey ELŐTT (F6/A03) ────────
    // ⚠⚠ Tulajdonosi szabály: MINDEN utalásnál külön, friss passkey — a jegy CSAK az itt
    //   felsorolt kérésekre, mindegyikre egyszer jó. Egy kattintás = egy passkey = ez a
    //   lista. Az újraküldés (IBAN-megerősítés után) új listát és új passkeyt kér.
    const plan = [];   // { kind, endpoint, body, docs, src? }
    const today = new Date().toISOString().slice(0,10);
    for (const doc of payDocs) {
      const src = sourceObjFor(doc);
      if (src?.bank === 'raiffeisen') {
        plan.push({ kind: 'raif', endpoint: paymentsApi.PAY_ENDPOINTS.pay,
                    body: buildRaiffeisenBody(doc, src), docs: [doc] });
      }
    }
    const revDocs = payDocs.filter(d => sourceObjFor(d)?.bank !== 'raiffeisen');
    if (revDocs.length && useDrafts) {
      // Counterparty-k feloldása + payment-ek építése, majd account_id-nként
      // (= forrás Revolut számla / deviza) külön draft. A Revolut a /payment-drafts
      // endpointon CSAK akkor fogad el egy batch-et, ha minden payment account_id-je
      // azonos ("All payments must share the same 'account_id'").
      // ⚠ A counterparty-KERESÉS a passkey ELŐTT fut (a bank azonosítói a lenyomat
      //   részei); létrehozás itt NINCS (2026-09-24 — „Partner felvétele" lépés).
      const groups = new Map();   // account_id → [{ doc, payment, currency }]
      for (const doc of revDocs) {
        try {
          const { cpId, cpAccountId } = resolveCounterparty(doc);
          const accountId = mixedCurrencies ? findAccountForDoc(doc) : sourceAccountId;
          // ⚠ 2026-08-13: KANONIKUS (lapos) tétel-alak — ugyanaz, amit az
          //   egy-tételes `/payments/pay` kap. A draft-alakra fordítást a CONNECTOR
          //   végzi (`revolut_connector._to_draft_body`).
          const payment = {
            account_id: accountId,
            currency:   doc.currency || 'HUF',
            amount:     doc.payable_amount != null ? doc.payable_amount : doc.gross_amount,
            counterparty_id: cpId,
            counterparty_account_id: cpAccountId || undefined,
            reference:  buildReference(doc),
            // nyilvántartás (a bank felé nem megy) — a connector teszi a meta[]-ba
            document_id:   doc.id,
            creditor_name: doc.supplier_name,
            creditor_iban: doc.bank_account_iban?.replace(/\s/g, '').toUpperCase(),
            account_name:  allSources.find(s => s.id === accountId)?.name,
          };
          if (!groups.has(accountId)) groups.set(accountId, []);
          groups.get(accountId).push({ doc, payment, currency: payment.currency });
        } catch (e) {
          setResults(r => ({...r, [doc.id]: describePayError(e)}));
        }
      }
      for (const [accountId, items] of groups) {
        const groupDocs = items.map(i => i.doc);
        const payments  = items.map(i => i.payment);
        const cur       = items[0].currency;
        const title = (revDocs.length === 1)
          ? `PhenoFin – ${groupDocs[0].supplier_name || 'Utalás'}`
          : `PhenoFin – ${today} (${payments.length} db ${cur})`;
        plan.push({ kind: 'revBatch', endpoint: paymentsApi.PAY_ENDPOINTS.payBatch, docs: groupDocs, body: {
          bank: 'revolut', account_id: accountId,
          // M8: a forrás Revolut-kapcsolatának asset-id-je (draft-mód forrása).
          company_asset_id: allSources.find(s => s.id === accountId)?.company_asset_id ?? null,
          title, payments,
          // A06: a megerősítés KONKRÉT (bizonylat, IBAN) párokra szól — globális
          // `first_payment_confirm` jelzőt kötegen SOHA nem küldünk.
          first_payment_confirm: false,
          confirmed_ibans: groupDocs
            .filter(d => oneTimeFor(d))
            .map(d => ({ document_id: d.id, iban: normIban(d.bank_account_iban) })) } });
      }
    } else if (revDocs.length) {
      for (const doc of revDocs) {
        try {
          plan.push({ kind: 'revOne', endpoint: paymentsApi.PAY_ENDPOINTS.pay,
                      body: buildRevolutBody(doc), docs: [doc] });
        } catch (e) {
          setResults(r => ({...r, [doc.id]: describePayError(e)}));
        }
      }
    }

    // ── 2. EGY passkey a teljes tervre — a konkrét törzsekkel ──────────────────────
    // A step-up a köteg INDÍTÁSA ELŐTT fut (nem a 403-ra reagálva): félig végrehajtott
    // köteg így nem lehet. Megszakításkor semmi nem indul el.
    const ok = plan.length > 0 && await requirePaymentStepUp(
      payDocs.length === 1 ? 'Utalás indítása előtt' : `${payDocs.length} utalás indítása előtt`,
      plan.map(p => ({ endpoint: p.endpoint, body: p.body })));
    if (!ok) {
      // Megszakítva (vagy nincs indítható tétel): a „loading" sorok visszaállnak.
      setResults(r => {
        const n = { ...r };
        payDocs.forEach(d => {
          if (n[d.id] !== 'loading') return;          // pl. counterparty-hiba: marad
          if (results[d.id] === undefined) delete n[d.id]; else n[d.id] = results[d.id];
        });
        return n;
      });
      setIsPaying(false); setPaymentInFlight(false); return;
    }

    // ── 3. VÉGREHAJTÁS: pontosan a tervben szereplő objektumok mennek ki ─────────────
    for (const step of plan) {
      if (step.kind === 'raif' || step.kind === 'revOne') {
        const doc = step.docs[0];
        try {
          if (step.kind === 'raif') await payOneRaiffeisen(step.body);
          else await payOne(doc, step.body);
          setResults(r => ({...r, [doc.id]:'ok'}));
        } catch (e) {
          const gate = ibanGateInfo(e);
          if (gate) {
            // Nem indult el → a sor NEM hibát mutat, hanem a tétel előzetes állapotát
            // (a kifogás a `gateOverride`-ba kerül, lent).
            gateHits.push(...gate);
            clearRows([doc.id]);
            continue;
          }
          setResults(r => ({...r, [doc.id]: describePayError(e)}));
        }
        continue;
      }
      // revBatch
      const groupDocs = step.docs;
      try {
        // ⚠ 2026-08-13: a DISZPÉCSEREN keresztül (`/payments/pay-batch`). A kötegelés
        //   banki KÉPESSÉG, nem felületi döntés — a connector dönti el, a válasz
        //   `mode`-ja mondja meg, mi történt valóban.
        const res = await paymentsApi.payBatch(step.body);
        // ⚠ TÉTEL-SZINTŰ eredmény: a `per_item` módban (draft KI) egy tétel
        //   elbukhat a többi sikere mellett, ezért nem jelölünk vakon mindent OK-nak.
        const byDoc = new Map((res?.results || []).map(r => [r.document_id, r]));
        groupDocs.forEach(d => {
          const r = byDoc.get(d.id);
          setResults(prev => ({ ...prev, [d.id]: (!r || r.ok) ? 'ok' : 'err' }));
        });
        // ⚠ A draftról a `revolut_payments` sor tud (A6 szándék-rögzítés a bankhívás
        //   ELŐTT) — külön marker nem kell (2026-08-13).
      } catch (e) {
        const gate = ibanGateInfo(e);
        if (gate) {
          // A köteg ATOMI: egyetlen ismeretlen IBAN az egészet megállítja, és
          // NEM keletkezett draft. Ezért mindegyik tétel „megerősítésre vár".
          // A06: a backend az ÖSSZES kifogásolt tételt visszaadja (`items`).
          // A köteg egyik tétele sem indult el → mind visszakerül előzetes állapotba.
          gateHits.push(...gate);
          clearRows(groupDocs.map(d => d.id));
          continue;
        }
        const msg = describePayError(e, 'Draft létrehozási hiba');
        groupDocs.forEach(d => setResults(r => ({...r, [d.id]:msg})));
      }
    }

    setIsPaying(false);
    setPaymentInFlight(false);
    // ⚠⚠ 2026-08-25, élő hiba (#249): FÜGGETLENÜL attól, hogy a próbálkozás
    // sikerült-e, a bizonylat payment_state-je (fő lista ÉS a DocSlideOver
    // ['document', id] cache-e) elavulttá válhatott - akár egy sikeres
    // utalás, akár egy elutasított (REJECTED) VAGY nyitva maradt
    // (PROGRESS/SUBMITTING) próbálkozás után. Enélkül a slide-over "Utal"
    // gombja a régi állapotot mutatta, és egy második bankra kattintva a
    // dupla-utalás kapu állította meg (helyesen, de feleslegesen).
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['document'] });
    // ⚠ Ha az utaláskori IBAN-kapu mégis megállította (a preflight óta változott valami):
    // NEM zárjuk le a modált, és NEM „Utalás újraküldése" lesz a gomb — a kifogás a tétel
    // preflight-adatát felülírja, és az állapotgép a megfelelő előzetes állapotba lép
    // („Számlaszám megerősítése" / „Eseti jóváhagyás és utalás"). A preflightot is
    // frissítjük, hogy a többi tétel is a valós állapotot mutassa.
    if (gateHits.length) {
      setGateOverride(prev => {
        const n = { ...prev };
        for (const h of gateHits) {
          n[h.doc_id] = { document_id: h.doc_id, has_iban: true, iban_trusted: false,
                          error: h.error, confirmable: h.confirmable, reason: h.message,
                          iban_masked: maskIban(h.iban) };
        }
        return n;
      });
      setFlowNotice({ tone: 'warning', text: 'Az utalás nem indult el: a számlaszám '
        + 'megerősítésre szorul (közben változott az állapota). Lásd lent.' });
      refetchPreflight();
    }
    if (gateHits.length === 0) setAllDone(true);
    const eventName = willCreateDrafts ? 'payment_draft_created' : 'payment_initiated';
    posthog?.capture(eventName, {
      doc_count: docs.length,
      // ⚠ Az analitikában a TÉNYLEGES bank kell, nem egy kétértékű feltevés. A
      //   korábbi `isRaif ? 'raiffeisen' : 'revolut'` egy harmadik banknál némán
      //   revolutnak jelentette volna — egy elrontott címke később kideríthetetlen
      //   (`.claude/rules/posthog-analitika.md`: hiányzó címke jobb, mint hamis).
      bank: banksUsed.length === 1 ? banksUsed[0]
                                   : (banksUsed.length ? 'vegyes' : null),
      is_draft: willCreateDrafts,
      currencies: [...new Set(docs.map(d => d.currency || 'HUF'))],
    });
    // NEM zárjuk be automatikusan — a user látja az eredményt, és kézzel csukja be
  };

  const fmtA = (v,cur='HUF') => v==null?'–':`${huFmt(v,cur==='HUF'?0:2)} ${cur}`;
  const totalsByCurrency = docs.reduce((acc,d)=>{
    const cur = d.currency||'HUF';
    acc[cur] = (acc[cur]||0) + ((d.payable_amount != null ? d.payable_amount : d.gross_amount)||0);
    return acc;
  }, {});

  const PAY_RED = '#ef4444';

  // ── AZ ALSÓ GOMB ÁLLAPOTGÉPE (2026-09-24, tulajdonosi kérés) ─────────────────────────
  // Tételenként: mi hiányzik még az utaláshoz. Csak a még NEM sikerült tételek számítanak
  // (egy részben lefutott köteg sikeres tétele nem kér semmit).
  const readinessOf = (doc) => {
    const iban = normIban(doc.bank_account_iban);
    const src = sourceObjFor(doc);
    const pf = gateOverride[doc.id] || preflightByDoc[doc.id];
    const key = ibanPairKey(doc.id, iban);
    // Egy most, ebben a modalban adott tartós megerősítés azonnal számít (a preflight
    // frissítése előtt is) — különben a piros sor a megerősítés után is ott maradna.
    const confirmedNow = pairConfirm[key]?.state === 'done';
    const untrusted = !!iban && pf?.iban_trusted === false && !confirmedNow;
    return {
      doc, iban, src, pf, key, confirmedNow, untrusted,
      isRevolut: src?.bank === 'revolut',
      hasCp: !!iban && cpByIban.has(iban),
      needsCp: src?.bank === 'revolut' && !!iban && cpsFetched && !cpsError && !cpByIban.has(iban),
      needsConfirm: untrusted && !!pf.confirmable,
      // Nincs mihez tartósan rögzíteni (partner nélküli bizonylat, pénzügyi képviselő) →
      // csak erre az utalásra hagyható jóvá.
      needsOneTime: untrusted && !pf.confirmable,
      oneTimeGiven: !!oneTimeOk[key],
      conflict: pf?.error === 'iban_ellentmondas',
    };
  };
  const readiness = docs.filter(d => results[d.id] !== 'ok').map(readinessOf);
  const readinessByDoc = Object.fromEntries(readiness.map(r => [r.doc.id, r]));
  const needCp = readiness.filter(r => r.needsCp);
  const needCpCount = new Set(needCp.map(r => r.iban)).size;
  // IBAN-onként egy partner-űrlap (két bizonylat ugyanarra a számlára = egy felvétel).
  const cpForms = [...new Map(needCp.map(r => [r.iban, r])).values()].map(r => {
    const draft = cpDraftFor(r.doc);
    return { r, draft, errs: counterpartyFieldErrors(draft) };
  });
  const cpFormsInvalid = cpForms.some(f => Object.keys(f.errs).length > 0);
  const needConfirm = readiness.filter(r => r.needsConfirm);
  const needOneTime = readiness.filter(r => r.needsOneTime);
  const cpsNeeded = readiness.some(r => r.isRevolut && r.iban);
  const sourcesChosen = mixedCurrencies
    ? currencies.every(c => !!sourceByCurrency[c]) : !!sourceAccountId;
  // Nevesített állapotok (`designing-the-user-surface`: minden ágnak neve van):
  //   done            → „Bezárás" (az eredmény látszik)
  //   loading         → „Ellenőrzés…" (tiltva) — a preflight vagy a Revolut-partnerlista még jön
  //   check_failed    → „Ellenőrzés újra" — nem tudjuk, mi hiányzik, ezért nem is ajánlunk utalást
  //   needs_partner   → „Partner felvétele" / „N partner felvétele" (friss passkey)
  //   needs_confirm   → „Számlaszám megerősítése" / „N számlaszám megerősítése" (friss passkey)
  //   needs_one_time  → „Eseti jóváhagyás és utalás" (pipa tételenként + utalási passkey)
  //   ready           → „Utalás indítása" / „DRAFT előkészítése" (utalási passkey)
  const flowState = allDone ? 'done'
    : (payDocIdList.length > 0 && !preflightFetched) || (cpsNeeded && !cpsFetched) ? 'loading'
    : preflightError || (cpsNeeded && cpsError) ? 'check_failed'
    : needCp.length ? 'needs_partner'
    : needConfirm.length ? 'needs_confirm'
    : needOneTime.length ? 'needs_one_time'
    : 'ready';

  // Az utalás kapuja: forrásszámla + adó-közlemény megerősítés + az állapotgép (csak
  // `ready`, vagy `needs_one_time` úgy, hogy MINDEN ilyen tételt egyenként jóváhagytak —
  // egy jóváhagyás sosem old fel egy másik tételt).
  const payDisabled = !sourcesChosen
    || (mismatchDocs.length > 0 && !taxRefConfirmed)
    || !(flowState === 'ready'
         || (flowState === 'needs_one_time' && needOneTime.every(r => r.oneTimeGiven)))
    || stepBusy;

  // Egyetlen, KIVÁLASZTOTT forrás, ami nem tud visszavonható draftot → azonnali utalás.
  // ⚠ A banknevet CSAK a címkéhez használjuk (`selectedBank`), a DÖNTÉSHEZ nem: azt a
  //   backend képesség-flagje adja. Így egy új bank helyes feliratot kap anélkül, hogy
  //   ezt a fájlt bárki hozzáigazítaná.
  const isImmediateSingle = !mixedCurrencies && !!selectedSource
    && !(selectedSource.supports_revocable_draft ?? false);
  const BANK_LABEL = { raiffeisen: 'Raiffeisen', revolut: 'Revolut' };
  const immediateBankName = BANK_LABEL[selectedBank] || 'Banki';

  const modalTitle = isImmediateSingle
    ? (docs.length===1 ? `${immediateBankName} utalás`
                       : `${immediateBankName} utalás – ${docs.length} számla`)
    : willCreateDrafts
      ? (docs.length===1 ? 'Revolut DRAFT előkészítése' : `Revolut DRAFT – ${docs.length} számla`)
      : payMode === 'unknown'
        // Még nincs (minden) forrás kiválasztva — ne ígérjünk és ne fenyegessünk.
        ? (docs.length===1 ? 'Utalás' : `Utalás – ${docs.length} számla`)
        : anyImmediateBank
          // Vegyes köteg azonnali forrással: nem nevezünk bankot, mert több is érintett.
          ? (docs.length===1 ? 'Utalás indítása' : `Utalás – ${docs.length} számla`)
          : (docs.length===1 ? 'Revolut utalás' : `Revolut utalás – ${docs.length} számla`);

  // ⚠ A modal piros kerete CSAK akkor jár, ha TUDJUK, hogy azonnal megy a pénz. A
  //   korábbi `!willCreateDrafts` az „még nem választott forrást" állapotot is pirosra
  //   festette volna — a figyelmeztetés akkor ér valamit, ha nem szól feleslegesen.
  return (
    <Modal open={open} onClose={onClose}
      // Eredmény / futó utalás / kifogásolt IBAN mellett csak a gombbal zárható.
      dismissible={!isPaying && !stepBusy && Object.keys(results).length === 0}
      title={modalTitle}
      accentColor={payMode === 'immediate' ? PAY_RED : undefined}
      width={540}>

      {/* ⚠ NÉGY ág, és a sorrend számít. A `payMode === 'unknown'` ágon SEMMILYEN
          sáv nem jelenik meg: amíg nincs forrás, sem DRAFT-ot ígérni, sem véglegeset
          fenyegetni nem szabad. Korábban ez az állapot a piros „Végleges utalás"
          ágra esett — vagyis a modal a megnyitás pillanatában fenyegetett. */}
      {/* M1 (2026-09-04): a CÉGNÉV ténylegesen kiírva a pénzt mozgató sávokon —
          a többcéges felhasználó lássa, MELYIK cég nevében indul a pénz. Ugyanaz
          a `['company']` cache-bejegyzés, mint a step-up ablakban és a Layoutban. */}
      {isImmediateSingle ? (
        <div style={{marginBottom:'0.9rem',padding:'0.55rem 0.75rem',
          background:'rgba(239,68,68,0.1)',border:`1px solid ${PAY_RED}`,
          borderRadius:'var(--radius)',fontSize:'0.78rem',lineHeight:1.45,color:PAY_RED}}>
          <b>⚠ {immediateBankName} – azonnali utalás.</b> A megerősítéssel{companyInfo.name
            ? <> a <b>{companyInfo.name}</b> nevében</> : ''} {docs.length===1 ? 'a tétel' : `mind a ${docs.length} tétel`} <b>azonnal
          elindul</b>{selectedBank === 'raiffeisen' ? ' a jóváhagyott banki consent alapján' : ''} (nincs külön draft/jóváhagyás).
        </div>
      ) : willCreateDrafts ? (
        <div style={{marginBottom:'0.9rem',padding:'0.55rem 0.75rem',
          background:'rgba(59,130,246,0.08)',border:'1px solid rgba(59,130,246,0.35)',
          borderRadius:'var(--radius)',fontSize:'0.78rem',lineHeight:1.45}}>
          <b>DRAFT mód.</b> A {docs.length===1 ? 'tétel' : `${docs.length} tétel`}{companyInfo.name
            ? <> a <b>{companyInfo.name}</b> nevében</> : ''} egyetlen
          payment <b>DRAFT</b>-ba kerül – az utalás csak akkor megy ki, ha a Revolut Business
          appban <b>jóváhagyod</b>. (Kikapcsolható: Beállítások → Integrációk.)
        </div>
      ) : payMode === 'immediate' ? (
        <div style={{marginBottom:'0.9rem',padding:'0.55rem 0.75rem',
          background:'rgba(239,68,68,0.1)',border:`1px solid ${PAY_RED}`,
          borderRadius:'var(--radius)',fontSize:'0.78rem',lineHeight:1.45,color:PAY_RED}}>
          <b>⚠ Végleges utalás.</b> A megerősítéssel{companyInfo.name
            ? <> a <b>{companyInfo.name}</b> nevében</> : ''} {docs.length===1 ? 'a tétel' : `mind a ${docs.length} tétel`} <b>azonnal,
          jóváhagyás nélkül</b> elindul.{anyImmediateBank
            ? ' A kiválasztott bank nem ismer visszavonható draftot.'
            : ' Jóváhagyásos (DRAFT) módra váltás: Beállítások → Integrációk.'}
        </div>
      ) : null}

      {/* Forrás számla(k) */}
      <div style={{marginBottom:'1rem'}}>
        {!mixedCurrencies ? (
          <label style={{display:'flex',flexDirection:'column',gap:4}}>
            <span style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
              textTransform:'uppercase',letterSpacing:'0.06em'}}>
              Forrás számla{matchingAccounts.length > 0 ? ` · ${targetCurrency}` : ''}
            </span>
            <select value={sourceAccountId} onChange={e=>setSourceAccountId(e.target.value)}
              style={{background:'var(--bg-input)',border:'1px solid var(--border)',
                borderRadius:'var(--radius)',padding:'0.45rem 0.65rem',
                color:'var(--text)',fontSize:'0.85rem'}}>
              {displayAccounts.length===0&&(
                <option value="">
                  {paySources.length===0
                    ? (paySourcesFetched ? 'Nincs bekötött bankszámla-kapcsolat' : 'Betöltés…')
                    : 'Nincs alkalmas forrásszámla'}
                </option>)}
              {displayAccounts.map(a=>(
                <option key={a.id} value={a.id}>
                  {a.name}{a.balance != null ? ` – ${huFmt(a.balance, a.currency==='HUF'?0:2)} ${a.currency}` : ` · ${a.currency}`}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <span style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
              textTransform:'uppercase',letterSpacing:'0.06em'}}>
              Forrás számlák devizánként
            </span>
            {currencies.map(cur => {
              // ⚠ Nincs „ha nincs egyező, mutasd az összeset" tartalék — csak azok a
              //   számlák, amik a köteg összes ilyen devizájú tételét elviszik.
              const opts = sourcesForCurrency(cur);
              return (
                <label key={cur} style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{width:42,fontSize:'0.78rem',fontWeight:700,
                    fontFamily:'var(--mono)',color:'var(--text-muted)'}}>{cur}</span>
                  <select value={sourceByCurrency[cur]||''}
                    onChange={e=>setSourceByCurrency(m=>({...m,[cur]:e.target.value}))}
                    style={{flex:1,background:'var(--bg-input)',border:'1px solid var(--border)',
                      borderRadius:'var(--radius)',padding:'0.45rem 0.65rem',
                      color:'var(--text)',fontSize:'0.85rem'}}>
                    {opts.length===0&&<option value="">Betöltés...</option>}
                    {opts.map(a=>(
                      <option key={a.id} value={a.id}>
                        {a.name}{a.balance != null ? ` – ${huFmt(a.balance, a.currency==='HUF'?0:2)} ${a.currency}` : ` · ${a.currency}`}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
            <p style={{fontSize:'0.7rem',color:'var(--text-muted)',marginTop:2}}>
              Minden tétel a devizájának megfelelő fenti számláról kerül utalásra.
            </p>
          </div>
        )}
        {/* ⚠⚠ ÜRES ÁLLAPOT: azt mondjuk meg, MI HIÁNYZIK — Gábor kifejezett kérése.
            A korábbi szöveg itt „⚠ Nincs {deviza} devizájú forrás – más devizájú
            számla lett kiválasztva" volt: az egy FIGYELMEZTETÉS arról, hogy a felület
            épp olyat állított be, amit a bank elutasít. Most nem állítunk be ilyet,
            tehát nincs mire figyelmeztetni — helyette a hiányzó dolgot írjuk ki. */}
        {/* M8/M11: NINCS EGYETLEN forrás sem (betöltés után) — ez nem „nem illő
            deviza", hanem hiányzó bankszámla-KAPCSOLAT: a közös AssetRequiredNotice
            mondja ki, és a beállításra mutat. */}
        {paySourcesFetched && paySources.length === 0 && (
          <div style={{marginTop:6}}>
            <AssetRequiredNotice assetType="bank_account" label="Bankszámla" linkTo="/settings/banks">
              Nincs utalásra használható bankszámla-kapcsolat (Raiffeisen vagy Revolut API,
              engedélyezett utalással). Utalni csak bekötött és engedélyezett banki kapcsolatról lehet.
            </AssetRequiredNotice>
          </div>
        )}
        {eligibleSources.length === 0 && paySources.length > 0 && (
          <div style={{marginTop:6,padding:'0.5rem 0.65rem',
            background:'var(--bg-input)',border:'1px solid var(--border)',
            borderRadius:'var(--radius)'}}>
            <p style={{fontSize:'0.75rem',fontWeight:600,margin:0}}>
              Ez a tétel egyetlen számládról sem utalható.
            </p>
            <ul style={{margin:'4px 0 0 1rem',padding:0,fontSize:'0.72rem',
              color:'var(--text-muted)'}}>
              {missingRequirements.map(m => <li key={m}>{m}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Számlák listája */}
      <div style={{marginBottom:'1rem',maxHeight:280,overflowY:'auto',
        display:'flex',flexDirection:'column',gap:6}}>
        {docs.map(d => {
          const status = results[d.id];
          const rd = readinessByDoc[d.id];   // undefined = már sikerült (✓ OK)
          // ⚠ A sor az AKTUÁLIS állapotot mutatja (preflight + partnerlista + a modalban
          //   adott megerősítések) — nem egy korábbi 409 szövegét, ami megerősítés után is
          //   pirosan ott maradt (2026-09-24, élő eset).
          const cpLine = !d.bank_account_iban
            ? { color: 'var(--danger)', text: '⚠ Nincs IBAN – nem utalható' }
            : !rd || !rd.isRevolut ? null
            : !cpsFetched ? { color: 'var(--text-muted)', text: 'Revolut-partner ellenőrzése…' }
            : cpsError ? { color: 'var(--danger)', text: '⚠ A Revolut-partnerlista nem érhető el' }
            : rd.hasCp ? { color: 'var(--accent)', text: '✓ Partner létezik Revoluton' }
            : { color: 'var(--warning)', text: '+ Partner felvétele szükséges (lent, passkey-jel)' };
          const ibanLine = !rd || !rd.iban || !rd.pf ? null
            : rd.confirmedNow ? { color: 'var(--success)', text: '✓ Számlaszám megerősítve' }
            : rd.pf.iban_trusted ? { color: 'var(--text-muted)', text: '✓ Igazolt számlaszám' }
            : rd.needsOneTime && rd.oneTimeGiven
              ? { color: 'var(--warning)', text: '✓ Eseti jóváhagyás — csak erre az utalásra' }
            : rd.conflict
              ? { color: 'var(--danger)', text: '⚠ A cégjegyzék szerint nem a szállító számlaszáma — ellenőrzés szükséges' }
            : rd.needsConfirm
              ? { color: 'var(--warning)', text: '⚠ Nem igazolt számlaszám — megerősítés szükséges' }
              : { color: 'var(--warning)', text: '⚠ Nem igazolt számlaszám — eseti jóváhagyás szükséges' };
          return (
            <div key={d.id} style={{display:'flex',alignItems:'center',gap:10,
              padding:'0.5rem 0.65rem',background:'rgba(255,255,255,0.02)',
              borderRadius:'var(--radius)',border:'1px solid var(--border-dim)'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:'0.82rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {d.supplier_name||'Ismeretlen'}
                </div>
                <div style={{fontSize:'0.68rem',color:'var(--text-muted)',fontFamily:'var(--mono)',
                  overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {d.invoice_number||'–'} · {d.bank_account_iban ? fmtIban(d.bank_account_iban) : 'Nincs IBAN'}
                </div>
                {payrollIbanMismatch(d) && (
                  <div style={{fontSize:'0.66rem',marginTop:1,color:'var(--danger)',fontWeight:600}}>
                    ⚠ Bér-számlaszám eltér a munkavállalói törzstől
                  </div>
                )}
                {cpLine && (
                  <div style={{fontSize:'0.65rem',marginTop:1,color:cpLine.color}}>{cpLine.text}</div>
                )}
                {ibanLine && (
                  <div style={{fontSize:'0.65rem',marginTop:1,color:ibanLine.color}}>{ibanLine.text}</div>
                )}
                {/* KÖZLEMÉNY tételenként (Gábor, 2026-08-05): köteges utalásnál a
                    kézi mező elrejtve, mert egy szabad szöveg nem lehet helyes több
                    külön számlára — de a felhasználónak LÁTNIA kell, mi megy ki.
                    Ez ellenőrzés és bizalom, nem dísz. */}
                {(() => {
                  const mm = taxRefMismatch(d);
                  return (
                    <div style={{fontSize:'0.65rem',marginTop:2,
                      color: mm ? '#f59e0b' : 'var(--text-muted)',
                      overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}
                      title={buildReference(d)}>
                      {mm ? '⚠ ' : ''}Közlemény: <span style={{fontFamily:'var(--mono)'}}>
                        {buildReference(d)}</span>
                      {mm && <> — eltér a cég adószámától ({mm.expected})</>}
                    </div>
                  );
                })()}
              </div>
              <div style={{textAlign:'right',flexShrink:0}}>
                <div style={{fontFamily:'var(--mono)',fontWeight:700,fontSize:'0.85rem'}}>
                  {fmtA(d.payable_amount != null ? d.payable_amount : d.gross_amount, d.currency)}
                </div>
                {d.payable_amount != null && d.payable_amount !== d.gross_amount && (
                  <div style={{fontFamily:'var(--mono)',fontWeight:400,fontSize:'0.72rem',
                    color:'var(--text-muted)'}}>(Σ {fmtA(d.gross_amount,d.currency)})</div>
                )}
                <div style={{width:56,textAlign:'center',marginTop:2}}>
                  {status==='loading'&&<Spinner/>}
                  {status==='ok'&&<span style={{color:'var(--accent)',fontWeight:700,fontSize:'0.78rem'}}>✓ OK</span>}
                  {status&&status!=='loading'&&status!=='ok'&&(
                    <span style={{color:'var(--danger)',fontSize:'0.64rem',lineHeight:1.2,display:'block'}}>{status}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Közlemény (1 számla esetén) */}
      {docs.length===1&&(
        <Input label="Közlemény" value={reference}
          onChange={e=>setReference(e.target.value)}
          placeholder={docs[0].invoice_number||'Fizetési közlemény'}
          style={{marginBottom:'1rem'}}/>
      )}

      {/* Összesítő */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
        padding:'0.55rem 0',borderTop:'1px solid var(--border)',
        borderBottom:'1px solid var(--border)',marginBottom:'1rem'}}>
        <span style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>Összesen</span>
        <span style={{fontFamily:'var(--mono)',fontWeight:700,textAlign:'right'}}>
          {Object.entries(totalsByCurrency).map(([cur,amt])=>fmtA(amt,cur)).join(' + ')}
        </span>
      </div>

      {/* ADÓ-KÖZLEMÉNY ELTÉRÉS — kifejezett megerősítés kell (Gábor, 2026-08-05).
          Rossz közleményű adóbefizetés a NAV-nál nem párosul: úgy viselkedik, mintha
          be sem fizettük volna. Ezért nem írjuk felül a könyvelő javaslatát, de nem is
          fogadjuk el csendben. */}
      {mismatchDocs.length > 0 && !allDone && (
        <div style={{marginBottom:'1rem',padding:'0.6rem 0.75rem',
          borderRadius:'var(--radius)',background:'rgba(245,158,11,0.10)',
          border:'1px solid rgba(245,158,11,0.35)'}}>
          <div style={{fontSize:'0.78rem',fontWeight:600,color:'#f59e0b',marginBottom:4}}>
            ⚠ Adó-közlemény eltér a cég adószámától
          </div>
          <p style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.5,margin:'0 0 6px'}}>
            {mismatchDocs.length === 1 ? 'Egy tételnél' : `${mismatchDocs.length} tételnél`} a
            könyvelő által javasolt közlemény nem tartalmazza a cég adószámát
            (<span style={{fontFamily:'var(--mono)'}}>{companyTaxReference}</span>). Ez lehet
            szándékos (más adónem hivatkozása), de elgépelés is — rossz közleménnyel a NAV nem
            tudja beazonosítani a befizetést.
          </p>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:'0.76rem',
            cursor:'pointer'}}>
            <input type="checkbox" checked={taxRefConfirmed}
              onChange={e=>setTaxRefConfirmed(e.target.checked)} />
            Ellenőriztem, a közlemények így helyesek
          </label>
        </div>
      )}

      {/* Utalás ideje — az Összesen ALATT (Gábor sorrend-kérése, 2026-08-05).
          A konkrét napot a backend adja: a -M szabályok banki naptáron számolnak. */}
      {schedOpts.length > 0 && (
        <div style={{marginBottom:'1rem'}}>
          <label style={{display:'flex',flexDirection:'column',gap:4}}>
            <span style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
              textTransform:'uppercase',letterSpacing:'0.06em'}}>Utalás ideje</span>
            <select value={scheduleRule} onChange={e=>setScheduleRule(e.target.value)}
              style={{background:'var(--bg-input)',border:'1px solid var(--border)',
                borderRadius:'var(--radius)',padding:'0.45rem 0.65rem',
                color:'var(--text)',fontSize:'0.85rem'}}>
              {schedOpts.map(o => (
                <option key={o.rule} value={o.rule}
                  disabled={o.rule !== 'MOST' && !earliestDue}>
                  {o.rule === 'MOST' ? o.label : `${o.rule} – ${o.label}`}
                  {o.date ? ` (${o.date.replaceAll('-', '.')}.)` : ''}
                </option>
              ))}
            </select>
          </label>
          {/* ⚠ FIX MAGASSÁGÚ jelzés-sor: a tartalma vált, a modal mérete NEM.
              Enélkül a nem-banki nap / clamp üzenet megjelenésekor a modal
              átrajzolódott és „ugrott" (Gábor jelezte). */}
          <div style={{minHeight:'2.1em',marginTop:4,fontSize:'0.7rem',lineHeight:1.5}}>
            {schedSel?.clamped ? (
              <span style={{color:'#f59e0b'}}>⚠ {schedSel.reason}</span>
            ) : schedApprox && scheduleRule.endsWith('M') ? (
              <span style={{color:'#f59e0b'}}>⚠ {schedule.calendar[0].reason}</span>
            ) : schedSel && schedSel.is_banking_day === false ? (
              <span style={{color:'var(--text-muted)'}}>
                Ez a nap nem banki nap – a bank a következő elszámolási napon dolgozza fel.
              </span>
            ) : docs.length > 1 && scheduleRule !== 'MOST' ? (
              <span style={{color:'var(--text-muted)'}}>
                Több tétel: a dátum bizonylatonként a saját lejáratából számolódik
                (fent a legkorábbi látszik).
              </span>
            ) : !earliestDue && scheduleRule === 'MOST' ? (
              <span style={{color:'var(--text-muted)'}}>
                A bizonylatnak nincs fizetési határideje, ezért csak az azonnali utalás
                választható.
              </span>
            ) : <span>&nbsp;</span>}
          </div>
        </div>
      )}

      {/* K8/b: bér-utalás előtt jól látható figyelmeztetés, ha a bérjegyzék számlaszáma
          eltér a munkavállalói törzstől. Nem blokkol (a backend sem) — a döntés emberi. */}
      {payrollMismatchDocs.length > 0 && (
        <div style={{marginBottom:'1rem',padding:'0.6rem 0.75rem',borderRadius:'var(--radius)',
          background:'var(--danger-subtle)',border:'1px solid var(--danger)'}}>
          <div style={{fontSize:'0.78rem',fontWeight:600,color:'var(--danger)',marginBottom:4}}>
            ⚠ Bér-utalás: a számlaszám eltér a munkavállalói törzstől
          </div>
          <p style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.5,margin:'0 0 6px'}}>
            A bérszámfejtő utalási jegyzékén más számlaszám áll, mint amit a Beállítások →
            Munkavállalók alatt rögzítettetek. Az egyik elírás lehet — vagy valaki módosította.
            Utalás előtt kérdezd meg a munkavállalót (ne e-mailben válaszolva), és ha kell,
            javítsd a törzsadatot vagy a bizonylatot.
          </p>
          <ul style={{margin:0,paddingLeft:'1.1rem'}}>
            {payrollMismatchDocs.map(d => (
              <li key={d.id} style={{fontSize:'0.74rem',color:'var(--text)'}}>
                {d.supplier_name || 'Ismeretlen munkavállaló'}{' '}
                <span style={{fontFamily:'var(--mono)',color:'var(--text-muted)'}}>
                  ({maskIban(d.bank_account_iban) || 'nincs számlaszám'})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* REVOLUT-PARTNER ADATAI — 2026-09-24 (élő hiba: „postcode is empty"). A felismert
          értékek szerkeszthetők; a „Partner felvétele" gomb ezt a törzset küldi (és a passkey
          is erre szól). Kötelező a Revolut gyártói sémája szerint: név (≥ 2 betű), ország,
          és ha van cím, az irányítószám — üresen a gomb tiltott, a mezőnél nevesített hiba. */}
      {flowState === 'needs_partner' && cpForms.length > 0 && (
        <div style={{marginBottom:'0.75rem',padding:'0.6rem 0.75rem',borderRadius:'var(--radius)',
          background:'var(--bg-input)',border:'1px solid var(--border)'}}>
          <div style={{fontSize:'0.78rem',fontWeight:600,marginBottom:4,color:'var(--text)'}}>
            {cpForms.length === 1 ? 'A Revolutnak küldött partneradatok' : `${cpForms.length} új Revolut-partner adatai`}
          </div>
          <p style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.5,margin:'0 0 8px'}}>
            Ezekkel az adatokkal vesszük fel a partnert a Revoluton. A bizonylatból olvastuk ki
            őket — nézd át, és javítsd, ha valami nem stimmel. A passkey pontosan erre szól.
          </p>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {cpForms.map(({ r, draft, errs }) => {
              const lock = stepBusy || isPaying;
              const f = (field, label, extra = {}) => (
                <Input label={label} value={draft[field] || ''} error={errs[field]}
                  disabled={lock} maxLength={REVOLUT_ADDRESS_LIMITS[field]}
                  onChange={e => setCpField(r.doc, field, e.target.value)} {...extra} />
              );
              return (
                <div key={r.iban} style={{padding:'0.5rem 0.6rem',borderRadius:'var(--radius)',
                  background:'var(--bg-card)',
                  border:`1px solid ${cpErrors[r.iban] ? 'var(--danger)' : 'var(--border)'}`}}>
                  <div style={{fontSize:'0.7rem',color:'var(--text-muted)',marginBottom:6}}>
                    <span style={{fontFamily:'var(--mono)'}}>{maskIban(r.iban)}</span>
                    {' · '}{r.doc.currency || 'HUF'}
                    {draft.source === 'nav' && ' · a cím a NAV-adatból'}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:8}}>
                    <div style={{gridColumn:'1 / -1'}}>{f('name', 'Partner neve', { maxLength: 140 })}</div>
                    <div style={{gridColumn:'1 / -1'}}>{f('street_line1', 'Utca, házszám')}</div>
                    <div style={{gridColumn:'1 / -1'}}>{f('street_line2', 'Cím 2. sora (nem kötelező)')}</div>
                    {f('postcode', 'Irányítószám')}
                    {f('city', 'Város')}
                    {f('country', 'Ország (2 betű)', { maxLength: 2,
                      onChange: e => setCpField(r.doc, 'country', e.target.value.toUpperCase()) })}
                  </div>
                  {!errs.postcode && !String(draft.postcode || '').trim() && (
                    <div style={{fontSize:'0.7rem',color:'var(--text-muted)',marginTop:6,lineHeight:1.45}}>
                      Cím nélkül vesszük fel (a Revolut ezt engedi, de azt ajánlja, hogy minden
                      címmező ki legyen töltve — egyes utalási utak elutasíthatják a cím nélküli partnert).
                    </div>
                  )}
                  {cpErrors[r.iban] && (
                    <div style={{fontSize:'0.72rem',color:'var(--danger)',marginTop:6,lineHeight:1.45}}>
                      {cpErrors[r.iban]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* NEM IGAZOLT SZÁMLASZÁM — 2026-09-24 óta ELŐRE (preflight), nem egy utólagos 409-ből.
          A checksum-ellenőrzés csak az ÉRVÉNYTELEN IBAN-t fogja meg; egy hamisított
          számlán érvényes, de IDEGEN számlaszám is állhat. A döntés emberi — a tartós
          megerősítés a lenti gombbal (passkey), az eseti jóváhagyás tételenként itt. */}
      {(() => {
        const objections = readiness.filter(r => r.iban && r.pf?.iban_trusted === false);
        if (!objections.length || allDone) return null;
        const open = objections.filter(r => r.untrusted && !(r.needsOneTime && r.oneTimeGiven));
        const anyConflict = objections.some(r => r.conflict && !r.confirmedNow);
        return (
        <div style={{marginBottom:'1rem',padding:'0.6rem 0.75rem',
          borderRadius:'var(--radius)',
          background: open.length === 0 ? 'var(--bg-input)'
            : anyConflict ? 'var(--danger-subtle)' : 'var(--warning-subtle)',
          border: `1px solid ${open.length === 0 ? 'var(--border)'
            : anyConflict ? 'var(--danger)' : 'var(--warning)'}`}}>
          <div style={{fontSize:'0.78rem',fontWeight:600,marginBottom:4,
            color: open.length === 0 ? 'var(--success)'
              : anyConflict ? 'var(--danger)' : 'var(--warning)'}}>
            {open.length === 0
              ? (objections.length === 1 ? '✓ A számlaszám rendezve' : `✓ Mind a ${objections.length} számlaszám rendezve`)
              : <>⚠ {open.length === 1 ? 'Egy számlaszám nem igazolt' : `${open.length} számlaszám nem igazolt`}
                  {open.length < objections.length && ` (${objections.length - open.length} rendezve)`}</>}
          </div>
          {open.length > 0 && (
            <p style={{fontSize:'0.72rem',color:'var(--text-muted)',lineHeight:1.5,margin:'0 0 8px'}}>
              Ezekre a számlaszámokra még nem utaltunk, és egyik megbízható forrás (NAV, korábbi
              banki utalás, korábbi megerősítés) sem igazolja őket. Ez lehet szabályos (új szállító,
              számlaszám-váltás), de <b>hamisított vagy módosított számla</b> jele is. <b>Vesd össze
              a számlával</b> — lehetőleg a szállítóval is, független úton —, mielőtt megerősíted.
              Egy megerősítés csak az adott bizonylat adott számlaszámára szól.
            </p>
          )}
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {objections.map(r => {
              const pc = pairConfirm[r.key];
              return (
                <div key={r.key} style={{padding:'0.45rem 0.6rem',borderRadius:'var(--radius)',
                  background:'var(--bg-input)',
                  border:`1px solid ${r.conflict && r.untrusted ? 'var(--danger)' : 'var(--border)'}`}}>
                  <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontSize:'0.78rem',fontWeight:600,color:'var(--text)'}}>
                      {r.doc.supplier_name || 'Ismeretlen partner'}
                    </span>
                    <span style={{fontSize:'0.7rem',color:'var(--text-muted)',fontFamily:'var(--mono)'}}>
                      {r.doc.invoice_number || `#${r.doc.id}`}
                    </span>
                    <span style={{fontSize:'0.74rem',fontFamily:'var(--mono)',color:'var(--text)'}}
                      title="A teljes számlaszám a fenti tételsorban látható">
                      {r.pf.iban_masked || maskIban(r.iban)}
                    </span>
                  </div>
                  {r.confirmedNow ? (
                    <div style={{fontSize:'0.72rem',marginTop:6,color:'var(--success)',fontWeight:600}}>
                      ✓ Megerősítve — a számlaszám a partner megbízható számlái közé került.
                    </div>
                  ) : (
                    <>
                      {r.pf.reason && (
                        <p style={{fontSize:'0.7rem',lineHeight:1.45,margin:'4px 0 0',
                          color: r.conflict ? 'var(--danger)' : 'var(--text-muted)'}}>
                          {r.pf.reason}
                        </p>
                      )}
                      {r.needsConfirm ? (
                        <div style={{fontSize:'0.7rem',marginTop:6,color:'var(--text-muted)'}}>
                          {r.needsCp
                            ? 'A lenti „Partner felvétele” ezt a számlaszámot is megerősíti — ugyanazzal a passkey-jel.'
                            : 'Megerősítés a lenti „Számlaszám megerősítése” gombbal (passkey).'}
                        </div>
                      ) : (
                        <div style={{display:'flex',flexDirection:'column',gap:4,marginTop:6}}>
                          <span style={{fontSize:'0.7rem',color:'var(--text-muted)'}}>
                            {r.pf.error === 'penzugyi_kepviselo'
                              ? 'Pénzügyi képviselő számlája — tartósan nem rögzíthető a szállítónál.'
                              : 'Tartósan nem rögzíthető (a bizonylat nincs partnerhez kötve) — kösd partnerhez a Partnerek oldalon, vagy hagyd jóvá csak erre az utalásra.'}
                          </span>
                          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:'0.72rem',
                            cursor:'pointer',color:'var(--text)'}}>
                            <input type="checkbox" checked={r.oneTimeGiven}
                              disabled={isPaying || stepBusy}
                              onChange={e => setOneTimeOk(p => ({ ...p, [r.key]: e.target.checked }))} />
                            Csak erre az utalásra hagyom jóvá
                          </label>
                        </div>
                      )}
                    </>
                  )}
                  {pc?.state === 'error' && (
                    <div style={{fontSize:'0.7rem',marginTop:4,color:'var(--danger)'}}>{pc.msg}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}

      {/* Összesítő sor ha kész */}
      {allDone && (() => {
        const okCount = Object.values(results).filter(v => v==='ok').length;
        const errCount = Object.values(results).filter(v => v!=='ok'&&v!=='loading').length;
        return (
          <div style={{marginBottom:'0.75rem',padding:'0.5rem 0.75rem',borderRadius:'var(--radius)',
            background: errCount===0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${errCount===0 ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
            fontSize:'0.82rem'}}>
            {okCount > 0 && (
              <span style={{color:'var(--accent)',fontWeight:600}}>
                {willCreateDrafts
                  ? `✓ ${okCount} tétel DRAFT-ja elkészült. Jóváhagyás a Revolut Business appban (Payment drafts). `
                  : `✓ ${okCount} utalás elindítva. `}
              </span>
            )}
            {errCount > 0 && <span style={{color:'var(--danger)',fontWeight:600}}>⚠ {errCount} hiba. Lásd a részleteket fent.</span>}
          </div>
        );
      })()}

      {/* Az állapotgép magyarázó sora (elakadt lépés / utalás) — a gombok felett. */}
      {flowNotice && !allDone && (
        <div style={{marginBottom:'0.75rem',padding:'0.5rem 0.75rem',borderRadius:'var(--radius)',
          fontSize:'0.76rem',lineHeight:1.45,
          background: flowNotice.tone === 'danger' ? 'var(--danger-subtle)' : 'var(--warning-subtle)',
          border: `1px solid ${flowNotice.tone === 'danger' ? 'var(--danger)' : 'var(--warning)'}`,
          color: flowNotice.tone === 'danger' ? 'var(--danger)' : 'var(--text)'}}>
          {flowNotice.text}
        </div>
      )}
      {flowState === 'check_failed' && (
        <div style={{marginBottom:'0.75rem',fontSize:'0.74rem',color:'var(--danger)',lineHeight:1.45}}>
          Nem sikerült ellenőrizni, mi kell még az utaláshoz ({preflightError
            ? 'a számlaszámok ellenőrzése' : 'a Revolut-partnerlista'} nem érhető el). Amíg ez
          nincs meg, utalást nem ajánlunk fel.
        </div>
      )}

      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <Btn variant="ghost" onClick={allDone ? onSuccess : onClose}>
          {allDone?'Bezárás':'Mégsem'}
        </Btn>
        {/* ⚠⚠ AZ ALSÓ GOMB AZ ÁLLAPOTGÉPBŐL (2026-09-24, tulajdonosi kérés): a gomb azt a
            lépést kínálja, ami a legközelebbi hiányzó dolog — nem „Utalás”-t, ami utána
            409-cel megállna. Piros gomb csak ismert, azonnali utalásnál. */}
        {flowState === 'loading' && (
          <Btn icon={CreditCard} variant="primary" disabled loading>Ellenőrzés…</Btn>
        )}
        {flowState === 'check_failed' && (
          <Btn icon={RefreshCw} variant="primary" onClick={() => {
            if (preflightError) refetchPreflight();
            if (cpsError) qc.invalidateQueries({ queryKey: ['revolut-counterparties'] });
          }}>Ellenőrzés újra</Btn>
        )}
        {flowState === 'needs_partner' && (
          <Btn icon={UserPlus} variant="primary" loading={stepBusy}
            disabled={stepBusy || isPaying || !sourcesChosen || cpFormsInvalid}
            title={cpFormsInvalid ? 'Előbb töltsd ki a partner kötelező adatait (fent).' : undefined}
            onClick={() => runSetupStep('needs_partner', needCp)}>
            {needCpCount === 1 ? 'Partner felvétele' : `${needCpCount} partner felvétele`}
          </Btn>
        )}
        {flowState === 'needs_confirm' && (
          <Btn icon={ShieldCheck} variant="primary" loading={stepBusy}
            disabled={stepBusy || isPaying}
            onClick={() => runSetupStep('needs_confirm', needConfirm)}>
            {needConfirm.length === 1 ? 'Számlaszám megerősítése' : `${needConfirm.length} számlaszám megerősítése`}
          </Btn>
        )}
        {(flowState === 'needs_one_time' || flowState === 'ready') && (
          <Btn icon={CreditCard} variant={payMode === 'immediate' ? 'danger' : 'primary'}
            loading={isPaying} disabled={payDisabled||isPaying}
            onClick={handlePayAll}>
            {flowState === 'needs_one_time'
              ? 'Eseti jóváhagyás és utalás'
              : willCreateDrafts
                ? (readiness.length===1 ? 'DRAFT előkészítése' : `DRAFT előkészítése (${readiness.length})`)
                : (readiness.length===1 ? 'Utalás indítása'    : `Utalás indítása (${readiness.length})`)}
          </Btn>
        )}
      </div>
    </Modal>
  );
}


// ── Csak-olvasható mező ────────────────────────────────────────────────────
function ROField({ label, value, mono, children }) {
  const display = children ?? (value != null && value !== '' ? (
    <span style={mono ? {fontFamily:'var(--mono)',fontSize:'0.8rem'} : {fontSize:'0.82rem'}}>{value}</span>
  ) : <span style={{color:'var(--text-dim)',fontSize:'0.82rem'}}>–</span>);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:2}}>
      <span style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
        textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</span>
      {display}
    </div>
  );
}

// ── Bizonylat részletek modal ──────────────────────────────────────────────
function ModalPdfUpload({ doc }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [done, setDone] = useState(false);
  const uploadMut = useMutation({
    mutationFn: (file) => documentsApi.attachPdf(doc.id, file),
    onSuccess: () => {
      setDone(true);
      qc.invalidateQueries({ queryKey:['documents'] });
      qc.invalidateQueries({ queryKey:['accountant-check'] });
    },
  });
  if (done) {
    return <p style={{color:'var(--accent)',fontSize:'0.8rem',textAlign:'center'}}>
      PDF feltöltve ✓ (újranyitás után látszik az előnézet)
    </p>;
  }
  return (
    <div style={{textAlign:'center'}}>
      <p style={{color:'var(--text-dim)',fontSize:'0.8rem',marginBottom:10}}>Nincs csatolt fájl</p>
      <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{display:'none'}}
        onChange={e=>{ const f=e.target.files?.[0]; if (f) uploadMut.mutate(f); e.target.value=''; }}/>
      <Btn size="sm" variant="secondary" icon={Upload} loading={uploadMut.isPending}
        onClick={()=>fileRef.current?.click()}>PDF feltöltés</Btn>
      {uploadMut.isError && (
        <p style={{color:'var(--danger)',fontSize:'0.72rem',marginTop:6}}>Feltöltési hiba</p>
      )}
    </div>
  );
}


function DocSlideOver({ doc, onClose, onSave, loading, payable, onPay, onNav }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const [previewErr, setPreviewErr] = useState(false);
  const [allocForm, setAllocForm] = useState({ project_id:'', net_amount:'', notes:'' });
  const [suggestions, setSuggestions] = useState(null);
  const [pdfWarnings, setPdfWarnings] = useState(null);   // reextract eltérés-figyelmeztetések
  const [confirm, setConfirm] = useState(null);            // {title, message, danger, confirmLabel, action}
  // K-Q1 (2026-09-06): a NAV↔PDF eltérés nyugtázása — a NAV-adat marad, a teendő-listából kikerül.
  const ackMismatchMut = useMutation({
    mutationFn: () => documentsApi.ackNavMismatch(doc.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documents'] }); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
  });

  const fmtAllocNum = (v) => {
    if (v==null||v==='') return '';
    const isHuf = (doc?.currency||'HUF')==='HUF';
    return huFmt(v, isHuf?0:2) ?? '';
  };
  const parseAllocNum = (s) => {
    const raw = String(s||'').replace(/\s/g,'').replace(',','.');
    const n = parseFloat(raw);
    return isNaN(n) ? null : n;
  };

  useEffect(() => {
    if (doc) {
      setForm({});   // új bizonylat → a szerkesztő mezők ürítése (különben az előző
                     // bizonylaton beállított, de el nem mentett érték „átragad")
      setAllocForm({ project_id:'', net_amount:fmtAllocNum(doc.net_amount), notes:'' });
      setSuggestions(null);
      setPdfWarnings(null);
    }
  }, [doc?.id]);

  const { data: cats = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
    staleTime: 5*60*1000,
  });

  // Csatolt PDF leválasztása (téves párosítás visszavonása). A OneDrive fájl marad.
  const detachMut = useMutation({
    mutationFn: () => documentsApi.detachPdf(doc.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey:['documents'] });
      qc.invalidateQueries({ queryKey:['accountant-check'] });
      onClose();
    },
  });
  // Csatolt PDF újraolvasása: a NAV-ban hiányzó mezők (fizetendő, számlázott időszak,
  // bankszámla…) kitöltése + végösszeg-ellenőrzés. A meglévő értékeket nem írja felül.
  const reextractMut = useMutation({
    mutationFn: () => documentsApi.reextract(doc.id),
    onSuccess: (data) => {
      setPdfWarnings(data?.pdf_check_warnings ?? []);
      qc.invalidateQueries({ queryKey:['documents'] });
      qc.invalidateQueries({ queryKey:['accountant-check'] });
    },
  });
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.list,
    staleTime: 5*60*1000,
  });
  const { data: allocations = [], refetch: refetchAllocs } = useQuery({
    queryKey: ['allocations', doc?.id],
    queryFn: () => projectsApi.documentAllocations(doc.id),
    enabled: !!doc,
  });

  const addAllocMut = useMutation({
    mutationFn: d => projectsApi.createAllocation(doc.id, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['allocations', doc.id] });
      setAllocForm({ project_id:'', net_amount:fmtAllocNum(doc.net_amount), notes:'' });
      setSuggestions(null);
    },
  });
  const delAllocMut = useMutation({
    mutationFn: projectsApi.deleteAllocation,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allocations', doc.id] }),
  });
  const suggestMut = useMutation({
    mutationFn: () => projectsApi.suggestAllocations(doc.id),
    onSuccess: data => setSuggestions(data),
  });

  const catOptions = [{value:'',label:'– Nincs –'}, ...cats.map(c=>({value:c,label:c}))];
  const projectOptions = [{value:'',label:'– Projekt –'}, ...projects.map(p=>({
    value:p.id, label:p.client_name ? `${p.name} (${p.client_name})` : p.name
  }))];

  // A bélyegkép az aktív cég nevében, blobként (egy <img src="/api/..."> nem vinné a
  // cég-fejlécet → a legrégebbi tagság cégére esne → 404; components/AuthedFile.js).
  const thumb = useAuthedObjectUrl(
    doc && doc.pdf_onedrive_path && doc.pdf_filename ? previewUrl(doc.id) : null);

  if (!doc) return null;
  const f = k => form[k]!==undefined ? form[k] : (doc?.[k]??'');
  const set = (k, v) => setForm(p=>({...p,[k]:v}));
  const fmtDate = v => v ? v.slice(0,10).replace(/-/g,'.') + '.' : null;
  const fmtAmt = (v, cur='HUF') =>
    v==null ? null : `${huFmt(v,cur==='HUF'?0:2)} ${cur}`;

  const hasFile = !!doc.pdf_onedrive_path && !!doc.pdf_filename;
  // 2026-09-06: a saját proxy-n át — a webUrl csak tenant-belső Microsoft-fióknak nyílt.
  const fileLink = hasFile ? documentPdfUrl(doc.id) : (f('pdf_onedrive_url') || doc.pdf_onedrive_url);

  const handleSave = () => {
    const data = Object.fromEntries(Object.entries(form).filter(([,v])=>v!==undefined&&v!==''));
    onSave(data);
  };

  // Mentetlen módosítás → zárás előtt rákérdezünk (Esc / háttér / Mégse / ✕ is)
  const dirty = Object.keys(form).length > 0;
  const guardedClose = () => {
    if (dirty) {
      setConfirm({
        title: 'Nem mentett módosítások',
        message: 'A módosításaid nincsenek elmentve. Biztosan bezárod mentés nélkül?',
        danger: true,
        confirmLabel: 'Bezárás mentés nélkül',
        action: onClose,
      });
    } else onClose();
  };
  // ↑/↓: léptetés a lista sorai közt (csak ha nem űrlapmezőben áll a fókusz)
  const handleKey = (e) => {
    const tag = e.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (e.key === 'ArrowDown') { e.preventDefault(); onNav?.(1); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); onNav?.(-1); }
  };

  const partnerName = doc.direction==='outgoing'
    ? (doc.customer_name||'Kimenő bizonylat')
    : (doc.supplier_name||'Bejövő bizonylat');
  const isOverdue = doc.due_date && (doc.status==='pending'||doc.status==='overdue') && new Date(doc.due_date) < new Date();
  const displayAmt = doc.payable_amount != null ? doc.payable_amount : doc.gross_amount;

  const secS = { padding:'0.75rem 1.1rem', borderBottom:'1px solid var(--border-dim)' };
  const secH = { fontSize:'0.64rem', fontWeight:700, letterSpacing:'0.09em',
    textTransform:'uppercase', color:'var(--text-muted)', marginBottom:8 };
  const KV = ({ label, value, mono=true, color }) => (
    <div style={{display:'flex',justifyContent:'space-between',gap:12,padding:'2.5px 0',
      fontSize:'0.8rem',alignItems:'baseline'}}>
      <span style={{color:'var(--text-muted)',flexShrink:0}}>{label}</span>
      <span style={{fontFamily:mono?'var(--mono)':undefined,textAlign:'right',color,
        fontVariantNumeric:'tabular-nums',overflowWrap:'anywhere'}}>
        {(value !== null && value !== undefined && value !== '' && value !== '–')
          ? value : <span style={{color:'var(--text-dim)'}}>–</span>}
      </span>
    </div>
  );

  return (
    <SlideOver open onClose={guardedClose} width={480} ariaLabel={partnerName}
      onKeyDown={handleKey}>

      {/* Fejléc: partner + státusz + összeg + gyorsakció */}
      <div style={{padding:'0.85rem 1.1rem',borderBottom:'1px solid var(--border)'}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10}}>
          <div style={{minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:'0.95rem',fontWeight:700}}>{partnerName}</span>
              <DocTypeBadge type={doc.doc_type}/>
              <NavOpBadge doc={doc}/>
              <NavMismatchBadge doc={doc}/>
              <Badge variant={STATUS_COLORS[doc.status]||'default'}>
                {STATUS_LABELS[doc.status]||doc.status}
              </Badge>
            </div>
            {doc.nav_mismatch && (
              <div style={{marginTop:6,padding:'0.5rem 0.7rem',borderRadius:'var(--radius)',fontSize:'0.75rem',
                background: doc.nav_mismatch_seen_at ? 'rgba(148,163,184,0.10)' : 'rgba(245,158,11,0.12)',
                border:`1px solid ${doc.nav_mismatch_seen_at ? 'rgba(148,163,184,0.35)' : 'rgba(245,158,11,0.4)'}`}}>
                <div style={{fontWeight:700,marginBottom:2}}>
                  {doc.nav_mismatch_seen_at ? 'NAV↔PDF eltérés (nyugtázva)' : 'A NAV-adat és a számla PDF-je eltér'}
                </div>
                {doc.nav_mismatch.supplier_name && (
                  <div>Név: NAV „{doc.nav_mismatch.supplier_name.nav}” · PDF „{doc.nav_mismatch.supplier_name.pdf}”</div>
                )}
                {(doc.nav_mismatch.warnings||[]).map((w,i)=> <div key={i}>{w}</div>)}
                <div style={{color:'var(--text-muted)',marginTop:4}}>
                  A NAV-adat marad — vagy a számlával van baj, vagy az olvasat rossz. A partnernevet a partner-törzsben lehet
                  megerősíteni (pl. a NAV a pontot elhagyja: „Alzahu Kft” ← „Alza.hu Kft.”).
                </div>
                {!doc.nav_mismatch_seen_at && (
                  <div style={{marginTop:6}}>
                    <Btn size="sm" variant="outline" loading={ackMismatchMut.isPending}
                      onClick={()=>ackMismatchMut.mutate()}>Ellenőriztem, nyugtázom</Btn>
                  </div>
                )}
              </div>
            )}
            <div style={{fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-muted)',marginTop:2}}>
              {doc.invoice_number||'–'}{doc.supplier_tax_number ? ` · adószám ${doc.supplier_tax_number}` : ''}
            </div>
          </div>
          <button onClick={guardedClose} aria-label="Bezárás"
            style={{background:'none',border:'none',color:'var(--text-muted)',
              cursor:'pointer',fontSize:'1.1rem',lineHeight:1,flexShrink:0,padding:2}}>
            <X size={16}/>
          </button>
        </div>
        <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',
          gap:10,marginTop:8}}>
          <span style={{fontFamily:'var(--mono)',fontSize:'1.35rem',fontWeight:700,
            fontVariantNumeric:'tabular-nums'}}>
            {fmtAmt(displayAmt, doc.currency)}
            {doc.payable_amount!=null && doc.payable_amount!==doc.gross_amount && (
              <span style={{fontSize:'0.72rem',fontWeight:400,color:'var(--text-muted)',marginLeft:8}}>
                (Σ {fmtAmt(doc.gross_amount, doc.currency)})
              </span>
            )}
          </span>
          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:6}}>
            <span style={{fontFamily:'var(--mono)',fontSize:'0.7rem',color:'var(--text-muted)'}}>
              #{doc.id}
            </span>
            {payable && <Btn size="sm" icon={CreditCard} onClick={onPay}>Utal</Btn>}
          </div>
        </div>
      </div>

      {/* Görgethető törzs */}
      <div style={{flex:1,overflowY:'auto'}}>
        <div style={secS}>
          <p style={secH}>Dátumok</p>
          <KV label="Kelt" value={fmtDate(doc.issue_date)}/>
          <KV label="Teljesítés" value={fmtDate(doc.delivery_date)}/>
          <KV label="Fizetési határidő" value={fmtDate(doc.due_date)}
            color={isOverdue ? 'var(--danger)' : undefined}/>
          <KV label="Fizetve" value={fmtDate(doc.paid_date)}/>
          {(doc.billing_period_start||doc.billing_period_end)&&(
            <KV label="Számlázott időszak"
              value={`${fmtDate(doc.billing_period_start)||'?'} – ${fmtDate(doc.billing_period_end)||'?'}`}/>
          )}
          <KV label="Könyvelési időszak" value={doc.accounting_period}/>
        </div>

        <div style={secS}>
          <p style={secH}>Összegek · bank</p>
          <KV label="Nettó" value={fmtAmt(doc.net_amount,doc.currency)}/>
          <KV label="ÁFA" value={doc.vat_amount!=null
            ? `${fmtAmt(doc.vat_amount,doc.currency)}${doc.vat_percentage!=null?` (${doc.vat_percentage}%)`:''}`
            : null}/>
          <KV label="Bruttó" value={fmtAmt(doc.gross_amount,doc.currency)}/>
          {doc.payable_amount!=null && doc.payable_amount!==doc.gross_amount && (
            <KV label="Fizetendő (előleg után)" value={fmtAmt(doc.payable_amount,doc.currency)}/>
          )}
          {doc.currency!=='HUF'&&(
            <KV label="Árfolyam" value={doc.exchange_rate!=null?`${doc.exchange_rate} HUF`:null}/>
          )}
          <KV label="Bankszámlaszám" value={doc.bank_account_hu}/>
          <KV label="IBAN" value={doc.bank_account_iban ? fmtIban(doc.bank_account_iban) : null}/>
          <KV label="Fizetési mód" value={doc.payment_method} mono={false}/>
          <KV label="Forrás" value={doc.source} mono={false}/>
          {doc.notes && <KV label="Megjegyzés" value={doc.notes} mono={false}/>}
        </div>

        <div style={secS}>
          <p style={secH}>PDF · előnézet</p>
          {hasFile&&!previewErr&&!thumb.error?(
            <>
              {fileLink?(
                <AuthedLink href={fileLink} style={{display:'block',width:'100%'}}>
                  {thumb.src
                    ?<img src={thumb.src} alt="Bizonylat előnézet" onError={()=>setPreviewErr(true)}
                      style={{width:'100%',borderRadius:'var(--radius)',cursor:'pointer',
                        border:'1px solid var(--border)',objectFit:'contain',maxHeight:300}}/>
                    :<div style={{padding:'1rem 0',textAlign:'center'}}><Spinner/></div>}
                </AuthedLink>
              ):(
                thumb.src
                  ?<img src={thumb.src} alt="Bizonylat előnézet" onError={()=>setPreviewErr(true)}
                    style={{width:'100%',borderRadius:'var(--radius)',
                      border:'1px solid var(--border)',objectFit:'contain',maxHeight:300}}/>
                  :<div style={{padding:'1rem 0',textAlign:'center'}}><Spinner/></div>
              )}
              {fileLink&&(
                <AuthedLink href={fileLink}
                  style={{display:'inline-flex',alignItems:'center',gap:4,marginTop:6,
                    color:'var(--primary)',fontSize:'0.78rem',textDecoration:'none'}}>
                  <ExternalLink size={13}/> Megnyitás OneDrive-ban
                </AuthedLink>
              )}
            </>
          ):fileLink?(
            <div style={{textAlign:'center',padding:'0.5rem 0'}}>
              <p style={{color:'var(--text-muted)',fontSize:'0.8rem',marginBottom:10}}>
                {(previewErr||thumb.error)?'Előnézet nem elérhető':'Nincs feltöltött fájl'}
              </p>
              <AuthedLink href={fileLink}
                style={{display:'inline-flex',alignItems:'center',gap:4,
                  color:'var(--primary)',fontSize:'0.82rem'}}>
                <ExternalLink size={14}/> Megnyitás OneDrive-ban
              </AuthedLink>
            </div>
          ):(
            <ModalPdfUpload doc={doc}/>
          )}
          {doc.source_email_id && (
            <p style={{fontSize:'0.7rem',color:'var(--text-muted)',marginTop:6}}>
              Forrás: e-mail csatolmány/link (azonosító: {String(doc.source_email_id).slice(0,18)}…)
            </p>
          )}
          {hasFile && (
            <div style={{marginTop:10,display:'flex',gap:8,flexWrap:'wrap'}}>
              <Btn size="sm" variant="outline" icon={RefreshCw}
                loading={reextractMut.isPending}
                title="A PDF újbóli kiolvasása: a hiányzó mezők kitöltése, a végösszegek ellenőrzése. A meglévő értékeket nem írja felül."
                onClick={()=>reextractMut.mutate()}>
                PDF újraolvasása
              </Btn>
              <Btn size="sm" variant="ghost" icon={Trash2}
                disabled={detachMut.isPending}
                style={{color:'var(--danger)'}}
                onClick={()=>setConfirm({
                  title: 'PDF leválasztása',
                  message: 'Biztosan leválasztod a PDF-et erről a bizonylatról? '
                    + 'A bizonylat "nincs PDF" állapotba kerül (újra csatolható / '
                    + 'a megfelelő bizonylathoz rendelhető). A OneDrive-on a fájl megmarad.',
                  danger: true,
                  confirmLabel: 'Leválasztás',
                  action: () => detachMut.mutate(),
                })}>
                {detachMut.isPending ? 'Leválasztás…' : 'PDF leválasztása'}
              </Btn>
            </div>
          )}
          {reextractMut.isError && (
            <p style={{color:'var(--danger)',fontSize:'0.72rem',marginTop:6}}>
              Újraolvasási hiba{reextractMut.error?.response?.data?.detail
                ? `: ${reextractMut.error.response.data.detail}` : ''}
            </p>
          )}
          {pdfWarnings!==null && (
            pdfWarnings.length===0 ? (
              <p style={{color:'var(--accent)',fontSize:'0.74rem',marginTop:6}}>
                Újraolvasva ✓ – nincs végösszeg-eltérés
              </p>
            ) : (
              <div style={{marginTop:8,background:'var(--warning-subtle)',
                border:'1px solid rgba(245,158,11,0.35)',borderRadius:'var(--radius)',
                padding:'0.55rem 0.75rem'}}>
                <p style={{color:'var(--warning)',fontSize:'0.72rem',fontWeight:600,marginBottom:4}}>
                  ⚠ Végösszeg-eltérés a PDF és a rögzített érték között:
                </p>
                {pdfWarnings.map((w,i)=>(
                  <p key={i} style={{fontSize:'0.72rem',color:'var(--text-muted)',
                    fontFamily:'var(--mono)'}}>{w}</p>
                ))}
              </div>
            )
          )}
        </div>

        <div style={secS}>
          <p style={secH}>Szerkesztés</p>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <Select label="Státusz" value={f('status')} onChange={e=>set('status',e.target.value)}
                options={[
                  {value:'pending',label:'Fizetendő'},{value:'paid',label:'Fizetve'},
                  {value:'overdue',label:'Lejárt'},{value:'cancelled',label:'Törölt'},
                ]}/>
              <Input label="Kifizetés dátuma" type="date" value={f('paid_date')}
                onChange={e=>set('paid_date',e.target.value)}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <Select label="Kategória" value={f('category')} onChange={e=>set('category',e.target.value)}
                options={catOptions}/>
              <Input label="Könyvelési időszak" type="month" value={f('accounting_period')}
                onChange={e=>set('accounting_period',e.target.value)}/>
            </div>
            {/* B2 (2026-09-06): a kategória-javítás CSAK kérésre lesz szabály — egy egyszeri
                átsorolás nem szabály. Ha partner van, a partner alapértelmezése lesz; különben
                a szállító nevére/törzsszámára kötött szabály (Beállítások → Kategóriák → Szabályok). */}
            {form.category !== undefined && form.category !== doc.category && (
              <Checkbox label={`Legyen ez a szabály a szállítóhoz (${doc.supplier_name || doc.customer_name || 'ismeretlen'})`}
                checked={!!form.apply_as_rule}
                onChange={e=>set('apply_as_rule', e.target.checked)}/>
            )}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              <Input label="Számlázott időszak kezdete" type="date" value={f('billing_period_start')}
                onChange={e=>set('billing_period_start',e.target.value)}/>
              <Input label="Számlázott időszak vége" type="date" value={f('billing_period_end')}
                onChange={e=>set('billing_period_end',e.target.value)}/>
            </div>
            <Input label="OneDrive fájl link" placeholder="https://..."
              value={f('pdf_onedrive_url')} onChange={e=>set('pdf_onedrive_url',e.target.value)}/>
            <label style={{display:'flex',flexDirection:'column',gap:4}}>
              <span style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
                textTransform:'uppercase',letterSpacing:'0.06em'}}>Megjegyzés</span>
              <textarea value={f('notes')} onChange={e=>set('notes',e.target.value)}
                rows={3}
                style={{background:'var(--bg-input)',border:'1px solid var(--border)',
                  borderRadius:'var(--radius)',padding:'0.45rem 0.65rem',
                  color:'var(--text)',fontSize:'0.85rem',resize:'vertical',width:'100%'}}/>
            </label>
          </div>
        </div>

        {/* Projekt allokációk */}
        <div style={{...secS,borderBottom:'none'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
            <p style={{...secH,marginBottom:0}}>
              Projekt allokációk
              {doc.net_amount!=null&&(
                <span style={{fontWeight:400,marginLeft:8,textTransform:'none',letterSpacing:0}}>
                  {'(nettó: '}{huFmt(doc.net_amount,doc.currency==='HUF'?0:2)}{' '}{doc.currency}{')'}
                </span>
              )}
            </p>
            <Btn icon={Sparkles} size="sm" variant="ghost"
              loading={suggestMut.isPending} onClick={()=>suggestMut.mutate()}>
              AI javaslat
            </Btn>
          </div>

          {suggestions!==null&&(
            <div style={{background:'var(--primary-subtle)',border:'1px solid rgba(59,130,246,0.25)',
              borderRadius:'var(--radius)',padding:'0.65rem 0.85rem',marginBottom:10}}>
              <p style={{fontSize:'0.72rem',color:'var(--primary)',fontWeight:600,marginBottom:6}}>
                AI javaslat – kattints a hozzáadáshoz:
              </p>
              {suggestions.length===0
                ?<p style={{fontSize:'0.78rem',color:'var(--text-muted)'}}>Nem találtam projekthez köthető adatot.</p>
                :suggestions.map((s,i)=>(
                  <div key={i} style={{marginBottom:4}}>
                    <button onClick={()=>addAllocMut.mutate({project_id:s.project_id,net_amount:s.net_amount})}
                      style={{background:'rgba(59,130,246,0.15)',border:'1px solid rgba(59,130,246,0.4)',
                        borderRadius:'var(--radius)',padding:'0.25rem 0.65rem',cursor:'pointer',
                        color:'var(--primary)',fontSize:'0.78rem',fontWeight:600}}>
                      + {s.project_name}: {huFmt(s.net_amount,doc.currency==='HUF'?0:2)} {doc.currency}
                    </button>
                  </div>
                ))
              }
              <button onClick={()=>setSuggestions(null)}
                style={{background:'none',border:'none',color:'var(--text-dim)',cursor:'pointer',fontSize:'0.72rem',marginTop:4}}>
                Bezárás
              </button>
            </div>
          )}

          {allocations.length>0&&(
            <div style={{marginBottom:10}}>
              {allocations.map(a=>(
                <div key={a.id} style={{display:'flex',alignItems:'center',gap:8,
                  padding:'0.35rem 0',borderBottom:'1px solid var(--border-dim)',fontSize:'0.8rem'}}>
                  <div style={{flex:1,minWidth:0}}>
                    <span style={{fontWeight:600}}>{a.project_name}</span>
                    {a.client_name && <span style={{color:'var(--text-muted)',fontSize:'0.72rem'}}> · {a.client_name}</span>}
                    {a.notes && <span style={{color:'var(--text-dim)',fontSize:'0.72rem'}}> · {a.notes}</span>}
                  </div>
                  <span style={{fontFamily:'var(--mono)',whiteSpace:'nowrap'}}>
                    {huFmt(a.net_amount,(a.currency||doc.currency)==='HUF'?0:2)}{' '}{a.currency||doc.currency}
                    {a.currency&&a.currency!=='HUF'&&a.net_amount_huf&&(
                      <span style={{fontSize:'0.68rem',color:'var(--text-muted)'}}>
                        {' '}≈ {huFmt(a.net_amount_huf,0)} HUF
                      </span>
                    )}
                  </span>
                  <Btn variant="ghost" size="sm" icon={Trash2}
                    loading={delAllocMut.isPending}
                    onClick={()=>delAllocMut.mutate(a.id)}/>
                </div>
              ))}
            </div>
          )}

          <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
            <Select label="Projekt" value={allocForm.project_id}
              onChange={e=>setAllocForm(p=>({...p,project_id:e.target.value}))}
              options={projectOptions} style={{minWidth:150}}/>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              <span style={{fontSize:'0.65rem',fontWeight:600,color:'var(--text-muted)',
                textTransform:'uppercase',letterSpacing:'0.06em'}}>Nettó összeg</span>
              <div style={{display:'flex',alignItems:'stretch'}}>
                <input type="text" inputMode="numeric" value={allocForm.net_amount}
                  onChange={e=>{
                    const v = e.target.value.replace(/[^\d\s,\.]/g,'');
                    setAllocForm(p=>({...p,net_amount:v}));
                  }}
                  onBlur={()=>{
                    const n = parseAllocNum(allocForm.net_amount);
                    if (n!==null) setAllocForm(p=>({...p,net_amount:fmtAllocNum(n)}));
                  }}
                  onFocus={e=>e.target.select()}
                  style={{width:110,background:'var(--bg-input)',border:'1px solid var(--border)',
                    borderRight:'none',borderRadius:'var(--radius) 0 0 var(--radius)',
                    padding:'0.45rem 0.65rem',color:'var(--text)',fontSize:'0.85rem',
                    outline:'none',textAlign:'right'}}/>
                <span style={{display:'flex',alignItems:'center',background:'rgba(255,255,255,0.06)',
                  border:'1px solid var(--border)',borderRadius:'0 var(--radius) var(--radius) 0',
                  padding:'0 0.55rem',fontSize:'0.78rem',fontWeight:700,
                  color:'var(--text-muted)',whiteSpace:'nowrap'}}>
                  {doc.currency||'HUF'}
                </span>
              </div>
            </div>
            <Btn icon={Plus} size="sm" variant="secondary"
              loading={addAllocMut.isPending}
              onClick={()=>{
                if (!allocForm.project_id||!allocForm.net_amount) return;
                addAllocMut.mutate({
                  project_id:parseInt(allocForm.project_id),
                  net_amount:parseAllocNum(allocForm.net_amount),
                  notes:allocForm.notes||null,
                });
              }}>
              Hozzáad
            </Btn>
          </div>
          {addAllocMut.isError&&(
            <p style={{color:'var(--danger)',fontSize:'0.78rem',marginTop:6}}>
              {addAllocMut.error?.response?.data?.detail||'Hiba az allokáció mentésekor'}
            </p>
          )}
        </div>
      </div>

      {/* Lábléc: Mentés és bezárás / Mégse (M365-minta) */}
      <div style={{display:'flex',gap:8,padding:'0.8rem 1.1rem',
        borderTop:'1px solid var(--border)',background:'var(--bg-surface)'}}>
        <Btn style={{flex:1,justifyContent:'center'}} loading={loading} disabled={!dirty}
          onClick={handleSave}>
          Mentés és bezárás
        </Btn>
        <Btn variant="secondary" onClick={guardedClose}>Mégse</Btn>
      </div>

      <ConfirmDialog open={!!confirm} title={confirm?.title} message={confirm?.message}
        danger={confirm?.danger} confirmLabel={confirm?.confirmLabel}
        onCancel={()=>setConfirm(null)}
        onConfirm={()=>{ const a=confirm.action; setConfirm(null); a(); }}/>
    </SlideOver>
  );
}
